// Core chat context implementation
import useChatScroll from "@/hooks/useChatScroll";
import { useToast } from "@/hooks/useToast";

import {
  convertAssistantTools,
  streamProviderChat,
} from "@/lib/utils/ai/streaming";
import {
  saveAssistantMessage,
  saveFollowUpMessage,
  saveToolCallMessage,
  saveUserMessage,
} from "@/lib/utils/ai/streaming/databaseOperations";
import { fileUtils } from "@/lib/utils/fileUtils";
import TokenCounter from "@/lib/utils/TokenCounter";
import { createVectorStore } from "@/lib/utils/vectorStoreUtils";
import {
  useAssistantStore,
  useAuthStore,
  useModelStore,
  useThreadStore,
} from "@/store";
import {
  createEnhancedMessageUpdater,
  ensureCriticalMessagesPreserved,
} from "@/utils/messageHelpers";
import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Enhanced Deep Research prompts from OpenAI cookbook
const CLARIFYING_PROMPT = `You will be given a research task by a user. Your job is NOT to complete the task yet, but instead to ask clarifying questions that would help you or another researcher produce a more specific, efficient, and relevant answer.

GUIDELINES:
1. **Maximize Relevance**
- Ask questions that are *directly necessary* to scope the research output.
- Consider what information would change the structure, depth, or direction of the answer.

2. **Surface Missing but Critical Dimensions**
- Identify essential attributes that were not specified in the user's request (e.g., preferences, time frame, budget, audience).
- Ask about each one *explicitly*, even if it feels obvious or typical.

3. **Do Not Invent Preferences**
- If the user did not mention a preference, *do not assume it*. Ask about it clearly and neutrally.

4. **Use the First Person**
- Phrase your questions from the perspective of the assistant or researcher talking to the user (e.g., "Could you clarify..." or "Do you have a preference for...")

5. **Use a Bulleted List if Multiple Questions**
- If there are multiple open questions, list them clearly in bullet format for readability.

6. **Avoid Overasking**
- Prioritize the 3–6 questions that would most reduce ambiguity or scope creep. You don't need to ask *everything*, just the most pivotal unknowns.

7. **Include Examples Where Helpful**
- If asking about preferences (e.g., travel style, report format), briefly list examples to help the user answer.

8. **Format for Conversational Use**
- The output should sound helpful and conversational—not like a form. Aim for a natural tone while still being precise.

9. **Allow Opt-Out**
- End with a note that the user can proceed with the original query if they prefer not to provide additional details.

After generating the clarifying questions, add this text: "\n\nPlease provide any additional details that would help me tailor the research to your specific needs, or let me know if you'd like me to proceed with your original query as-is."`;

const REWRITING_PROMPT = `You will be given a research task by a user. Your job is to produce a set of instructions for a researcher that will complete the task. Do NOT complete the task yourself, just provide instructions on how to complete it.

GUIDELINES:
1. **Maximize Specificity and Detail**
- Include all known user preferences and explicitly list key attributes or dimensions to consider.
- It is of utmost importance that all details from the user are included in the instructions.

2. **Fill in Unstated But Necessary Dimensions as Open-Ended**
- If certain attributes are essential for a meaningful output but the user has not provided them, explicitly state that they are open-ended or default to no specific constraint.

3. **Avoid Unwarranted Assumptions**
- If the user has not provided a particular detail, do not invent one.
- Instead, state the lack of specification and guide the researcher to treat it as flexible or accept all possible options.

4. **Use the First Person**
- Phrase the request from the perspective of the user.

5. **Tables**
- If you determine that including a table will help illustrate, organize, or enhance the information in the research output, you must explicitly request that the researcher provide them.
Examples:
- Product Comparison (Consumer): When comparing different smartphone models, request a table listing each model's features, price, and consumer ratings side-by-side.
- Project Tracking (Work): When outlining project deliverables, create a table showing tasks, deadlines, responsible team members, and status updates.
- Budget Planning (Consumer): When creating a personal or household budget, request a table detailing income sources, monthly expenses, and savings goals.
Competitor Analysis (Work): When evaluating competitor products, request a table with key metrics, such as market share, pricing, and main differentiators.

6. **Headers and Formatting**
- You should include the expected output format in the prompt.
- If the user is asking for content that would be best returned in a structured format (e.g. a report, plan, etc.), ask the researcher to format as a report with the appropriate headers and formatting that ensures clarity and structure.

7. **Language**
- If the user input is in a language other than English, tell the researcher to respond in this language, unless the user query explicitly asks for the response in a different language.

8. **Sources**
- If specific sources should be prioritized, specify them in the prompt.
- For product and travel research, prefer linking directly to official or primary websites (e.g., official brand sites, manufacturer pages, or reputable e-commerce platforms like Amazon for user reviews) rather than aggregator sites or SEO-heavy blogs.
- For academic or scientific queries, prefer linking directly to the original paper or official journal publication rather than survey papers or secondary summaries.
- If the query is in a specific language, prioritize sources published in that language.

After rewriting the query, format your response as: "Thank you for the clarifications! Based on your input, I understand you're looking for research on:\n\n**[REWRITTEN QUERY]**\n\nI'll now begin the deep research process to provide you with a comprehensive, well-sourced analysis. This may take a few minutes as I search through multiple sources and synthesize the findings."`;

// Optimized Deep Research Event Handler for Frontend
class DeepResearchEventHandler {
  constructor() {
    this.activities = new Map(); // Use Map for O(1) lookups
    this.sources = new Map(); // Use Map for O(1) lookups by URL
    this.stats = {
      totalActivities: 0,
      totalSources: 0,
      analyzedSources: 0,
      startTime: Date.now(),
      estimatedActivities: 0,
      remainingActivities: 0,
      progressPercentage: 0,
    };
    this.lastUpdateTime = Date.now();
    this.updateThrottle = 100; // Minimum ms between UI updates
  }

  // Phase-based progress calculation
  calculateProgress() {
    const activities = Array.from(this.activities.values());

    // Check for specific research phases based on actual backend messages
    const hasPlanning = activities.some(
      (a) =>
        (a.type || "").includes("plan") ||
        /planning|plan/i.test(a.message || "") ||
        /research-started|planning-started/i.test(a.type || "") ||
        (a.type === "agent_event" && /plan/i.test(a.message || ""))
    );

    const hasSearch = activities.some(
      (a) =>
        (a.type || "").includes("search") ||
        /search|found.*results|web search/i.test(a.message || "") ||
        /query-started|search-completed/i.test(a.type || "") ||
        (a.type === "agent_event" && /search/i.test(a.message || ""))
    );

    const hasScraping = activities.some(
      (a) =>
        (a.type || "").includes("scrap") ||
        /scrap|scraping|scrape/i.test(a.message || "") ||
        /scraping-url|scrape-completed/i.test(a.type || "") ||
        (a.type === "agent_event" && /scrap/i.test(a.message || ""))
    );

    const hasCompilation = activities.some(
      (a) =>
        (a.type || "").includes("compilation") ||
        /compilation|compil/i.test(a.message || "") ||
        /compilation-started|compilation-completed/i.test(a.type || "") ||
        (a.type === "agent_event" && /compil/i.test(a.message || ""))
    );

    const hasSynthesis = activities.some(
      (a) =>
        /final analysis|synthesis|final-result/i.test(a.message || "") ||
        /final-result/i.test(a.type || "") ||
        (a.type === "agent_event" && /final|synthesis/i.test(a.message || ""))
    );

    let progress = 0;

    if (hasSynthesis) {
      progress = 95;
    } else if (hasCompilation) {
      progress = 85;
    } else if (hasScraping) {
      // Scraping phase: 40% base + up to 40% based on scraping activities
      const scrapingCount = activities.filter(
        (a) =>
          /scrap/i.test(a.message || "") ||
          (a.type || "").includes("scrap") ||
          /scraping-url|scrape-completed/i.test(a.type || "") ||
          (a.type === "agent_event" && /scrap/i.test(a.message || ""))
      ).length;
      progress = Math.min(80, 40 + scrapingCount * 6);
    } else if (hasSearch) {
      // Search phase: 15% base + up to 25% based on search activities
      const searchCount = activities.filter(
        (a) =>
          /search|found.*results/i.test(a.message || "") ||
          (a.type || "").includes("search") ||
          /query-started|search-completed|search-ranked/i.test(a.type || "") ||
          (a.type === "agent_event" && /search/i.test(a.message || ""))
      ).length;
      progress = Math.min(40, 15 + searchCount * 4);
    } else if (hasPlanning) {
      progress = 10;
    } else if (activities.length > 0) {
      progress = 5;
    }

    // Ensure progress never decreases and caps at 99%
    this.stats.progressPercentage = Math.max(
      this.stats.progressPercentage || 0,
      Math.min(99, Math.round(progress))
    );

    console.log(
      `🔬 Progress calculation: ${progress}% (${activities.length} activities, planning: ${hasPlanning}, search: ${hasSearch}, scraping: ${hasScraping}, compilation: ${hasCompilation}, synthesis: ${hasSynthesis})`
    );

    return this.stats.progressPercentage;
  }

  handleEvent(event) {
    const now = Date.now();

    // Throttle updates to prevent UI flooding
    if (
      now - this.lastUpdateTime < this.updateThrottle &&
      event.type !== "complete"
    ) {
      return null;
    }

    // Unwrap agent_event wrapper from custom endpoint
    if (event.type === "agent_event" && event.data?.event) {
      // Transform: {type: "agent_event", data: {event: "scrape-completed", ...}}
      // To: {type: "scrape-completed", data: {...}}
      event = {
        type: event.data.event,
        data: event.data,
      };
    }

    this.lastUpdateTime = now;

    switch (event.type) {
      case "activity":
        return this.handleActivityEvent(event.data);
      case "progress":
        return this.handleProgressEvent(event.data);
      case "complete":
        return this.handleCompleteEvent(event.data);
      case "error":
        return this.handleErrorEvent(event.data);
      // SDK-based research events
      case "research-started":
        return this.handleResearchStartedEvent(event.data);
      case "planning-started":
        return this.handlePlanningStartedEvent(event.data);
      case "plan":
        return this.handlePlanEvent(event.data);
      case "agents-created":
        return this.handleAgentsCreatedEvent(event.data);
      case "agent_event":
        return this.handleAgentEvent(event.data);
      case "handoff_occurred":
        return this.handleHandoffEvent(event.data);
      case "compilation-started":
        return this.handleCompilationStartedEvent(event.data);
      case "compilation-completed":
        return this.handleCompilationCompletedEvent(event.data);
      case "sdk_event":
        return this.handleSDKEvent(event.data);
      case "scraping-url":
        return this.handleScrapingUrlEvent(event.data);
      case "scraping-error":
        return this.handleScrapingErrorEvent(event.data);
      case "scrape-completed":
        return this.handleScrapeCompletedEvent(event.data);
      case "search-ranked":
        return this.handleSearchRankedEvent(event.data);
      case "source-found":
        return this.handleSourceFoundEvent(event.data);
      case "research-timeout":
        return this.handleResearchTimeoutEvent(event.data);
      case "extraction-completed":
        return this.handleExtractionCompletedEvent(event.data);
      default:
        console.log(
          "🔬 Unhandled research event type:",
          event.type,
          event.data
        );
        return null;
    }
  }

  handleActivityEvent(data) {
    const updates = {};
    let hasUpdates = false;

    // Handle new activity efficiently
    if (data.latestActivity) {
      const activity = {
        ...data.latestActivity,
        timestamp: data.latestActivity.timestamp || new Date().toISOString(),
        id: data.latestActivity.id || `activity_${Date.now()}_${Math.random()}`,
      };

      // Only add if it's truly new
      if (!this.activities.has(activity.id)) {
        this.activities.set(activity.id, activity);
        this.stats.totalActivities = this.activities.size;
        updates.newActivity = activity;
        hasUpdates = true;
      }
    }

    // Handle sources efficiently with smart merging
    if (data.newSources && Array.isArray(data.newSources)) {
      const updatedSources = [];

      data.newSources.forEach((source) => {
        if (!source.url) return; // Skip invalid sources

        const existing = this.sources.get(source.url);

        if (!existing || this.shouldUpdateSource(existing, source)) {
          const mergedSource = {
            ...existing,
            ...source,
            lastUpdated: Date.now(),
            // Preserve original discovery metadata
            discoveredAt:
              existing?.discoveredAt ||
              source.discoveredAt ||
              new Date().toISOString(),
            activityType: existing?.activityType || source.activityType,
          };

          this.sources.set(source.url, mergedSource);
          updatedSources.push(mergedSource);
          hasUpdates = true;
        }
      });

      if (updatedSources.length > 0) {
        updates.updatedSources = updatedSources;
        this.stats.totalSources = this.sources.size;
        this.stats.analyzedSources = Array.from(this.sources.values()).filter(
          (s) => s.analyzed
        ).length;
      }
    }

    // Update backend progress stats
    if (data.estimatedActivities !== undefined) {
      this.stats.estimatedActivities = data.estimatedActivities;
      hasUpdates = true;
    }
    if (data.remainingActivities !== undefined) {
      this.stats.remainingActivities = data.remainingActivities;
      hasUpdates = true;
    }
    if (data.progressPercentage !== undefined) {
      this.stats.progressPercentage = data.progressPercentage;
      hasUpdates = true;
    }

    // Update progress heuristically if backend didn't provide it
    this.calculateProgress();

    return hasUpdates
      ? {
          ...updates,
          stats: { ...this.stats },
          elapsedTime: Date.now() - this.stats.startTime,
          status: "active",
        }
      : null;
  }

  shouldUpdateSource(existing, incoming) {
    // Smart update logic - only update if there's new information
    if (!existing) return true;
    if (incoming.analyzed && !existing.analyzed) return true;
    if (incoming.summary && !existing.summary) return true;
    if (incoming.updateType === "analyzed") return true;
    if (incoming.content && !existing.content) return true;
    if (incoming.description && !existing.description) return true;
    return false;
  }

  handleProgressEvent(data) {
    // Efficiently update only progress stats
    let hasUpdates = false;
    const updates = {};

    if (
      data.estimatedActivities !== undefined &&
      data.estimatedActivities !== this.stats.estimatedActivities
    ) {
      this.stats.estimatedActivities = data.estimatedActivities;
      hasUpdates = true;
    }
    if (
      data.remainingActivities !== undefined &&
      data.remainingActivities !== this.stats.remainingActivities
    ) {
      this.stats.remainingActivities = data.remainingActivities;
      hasUpdates = true;
    }
    if (
      data.progressPercentage !== undefined &&
      data.progressPercentage !== this.stats.progressPercentage
    ) {
      this.stats.progressPercentage = data.progressPercentage;
      hasUpdates = true;
    }

    return hasUpdates
      ? {
          stats: { ...this.stats },
          elapsedTime: Date.now() - this.stats.startTime,
          status: "active",
        }
      : null;
  }

  handleCompleteEvent(data) {
    console.log("🔬 Handling complete event:", data);
    const completedAt = Date.now();

    const result = {
      isComplete: true,
      status: "complete",
      finalAnalysis:
        data.finalAnalysis || data.analysis || "Research completed.",
      followUpQuestions: data.followUpQuestions || [],
      activities: Array.from(this.activities.values()),
      sources: Array.from(this.sources.values()),
      sourceLinks: Array.from(this.sources.values()).map((source) => ({
        url: source.url,
        title: source.title,
        description: source.description,
        discoveredAt: source.discoveredAt,
        depth: source.depth,
        activityType: source.activityType,
      })),
      stats: {
        ...this.stats,
        completedAt,
        totalDuration: completedAt - this.stats.startTime,
        endTime: completedAt,
      },
      elapsedTime: completedAt - this.stats.startTime,
    };

    console.log("🔬 Complete event result:", result);
    return result;
  }

  handleErrorEvent(data) {
    return {
      isError: true,
      status: "error",
      error: data.error || data.message || "Unknown error occurred",
      activities: Array.from(this.activities.values()),
      sources: Array.from(this.sources.values()),
      stats: {
        ...this.stats,
        errorAt: Date.now(),
      },
      elapsedTime: Date.now() - this.stats.startTime,
    };
  }

  // SDK-based research event handlers
  handleResearchStartedEvent(data) {
    console.log("🔬 Research started:", data);
    return {
      status: "active",
      message: data.message || "Research started",
      sdk: data.sdk || false,
    };
  }

  handlePlanningStartedEvent(data) {
    console.log("🔬 Planning started:", data);
    return {
      status: "active",
      message: data.message || "Planning research strategy",
      sdk: data.sdk || false,
    };
  }

  handlePlanEvent(data) {
    console.log("🔬 Research plan created:", data);
    return {
      status: "active",
      message:
        data.message ||
        `Research plan created with ${data.plan?.tasks?.length || 0} tasks`,
      sdk: data.sdk || false,
    };
  }

  handleAgentsCreatedEvent(data) {
    console.log("🔬 Agents created:", data);
    return {
      status: "active",
      message:
        data.message ||
        `${data.agents?.length || 0} research agents initialized`,
      sdk: data.sdk || false,
    };
  }

  handleAgentEvent(data) {
    console.log("🔬 Agent event:", data);

    // Create activity from agent event
    const activity = {
      id: `agent_${Date.now()}_${Math.random()}`,
      type: "agent_activity",
      status: "in_progress",
      message:
        data.message ||
        `${data.agentName || "Agent"} ${data.event || "working"}`,
      timestamp: new Date().toISOString(),
      agentId: data.agentId,
      agentName: data.agentName,
      event: data.event,
    };

    // Add to activities if not already present
    if (!this.activities.has(activity.id)) {
      this.activities.set(activity.id, activity);
      this.stats.totalActivities = this.activities.size;
    }

    return {
      status: "active",
      newActivity: activity,
      sdk: data.sdk || false,
    };
  }

  handleHandoffEvent(data) {
    console.log("🔬 Agent handoff:", data);

    const activity = {
      id: `handoff_${Date.now()}_${Math.random()}`,
      type: "handoff",
      status: "completed",
      message: data.message || `Handoff: ${data.fromAgent} → ${data.toAgent}`,
      timestamp: new Date().toISOString(),
      fromAgent: data.fromAgent,
      toAgent: data.toAgent,
      transitionType: data.transitionType,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    return {
      status: "active",
      newActivity: activity,
      sdk: data.sdk || false,
    };
  }

  handleCompilationStartedEvent(data) {
    console.log("🔬 Compilation started:", data);

    const activity = {
      id: `compilation_${Date.now()}_${Math.random()}`,
      type: "compilation",
      status: "in_progress",
      message: data.message || "Starting compilation of findings and sources",
      timestamp: new Date().toISOString(),
      findingsCount: data.findingsCount || 0,
      sourcesCount: data.sourcesCount || 0,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  handleCompilationCompletedEvent(data) {
    console.log("🔬 Compilation completed:", data);

    const activity = {
      id: `compilation_completed_${Date.now()}_${Math.random()}`,
      type: "compilation",
      status: "completed",
      message: data.message || "Compilation completed",
      timestamp: new Date().toISOString(),
      findingsCount: data.findingsCount || 0,
      sourcesCount: data.sourcesCount || 0,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  handleScrapingUrlEvent(data) {
    console.log("🔬 Scraping URL:", data);

    const activity = {
      id: `scraping_${Date.now()}_${Math.random()}`,
      type: "scraping",
      status: "in_progress",
      message: data.message || `Scraping URL: ${data.url || "unknown"}`,
      timestamp: new Date().toISOString(),
      url: data.url,
      agentId: data.agentId,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    // Add source immediately when scraping starts (not just when completed)
    // This allows sources to show up in real-time in the UI
    if (data?.url) {
      const sourceId = data.url;
      const source = {
        id: sourceId,
        url: data.url,
        title: data.title || data.url,
        timestamp: new Date().toISOString(),
        agentId: data.agentId,
        agentName: data.agentName,
      };
      this.sources.set(sourceId, source);
      this.stats.totalSources = this.sources.size;
    }

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  handleScrapingErrorEvent(data) {
    console.log("🔬 Scraping error:", data);

    const activity = {
      id: `scraping_error_${Date.now()}_${Math.random()}`,
      type: "scraping",
      status: "error",
      message:
        data.message || `Scraping error: ${data.error || "unknown error"}`,
      timestamp: new Date().toISOString(),
      error: data.error,
      agentId: data.agentId,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  handleScrapeCompletedEvent(data) {
    console.log("🔬 Scrape completed:", data);

    const activity = {
      id: `scrape_completed_${Date.now()}_${Math.random()}`,
      type: "scraping",
      status: "completed",
      message:
        data.message ||
        `Scrape completed: ${data.title || data.url} (${data.contentLength || 0} chars)`,
      timestamp: new Date().toISOString(),
      contentLength: data.contentLength,
      agentId: data.agentId,
      url: data.url,
      title: data.title,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    // Update source with completed data (source was already added in handleScrapingUrlEvent)
    if (data?.url) {
      const sourceId = data.url;
      const existingSource = this.sources.get(sourceId);
      const source = {
        ...existingSource, // Keep existing data
        id: sourceId,
        url: data.url,
        title: data.title || existingSource?.title || data.url, // Prefer new title
        contentLength: data.contentLength || 0,
        timestamp: existingSource?.timestamp || new Date().toISOString(), // Keep original timestamp
      };
      this.sources.set(sourceId, source);
      this.stats.totalSources = this.sources.size;
    }

    // Recalculate progress
    this.calculateProgress();

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  handleSearchRankedEvent(data) {
    console.log("🔬 Search ranked:", data);

    const activity = {
      id: `search_ranked_${Date.now()}_${Math.random()}`,
      type: "search",
      status: "completed",
      message:
        data.message || `Ranked ${data.resultCount || 0} results by relevance`,
      timestamp: new Date().toISOString(),
      resultCount: data.resultCount,
      topScore: data.topScore,
      agentId: data.agentId,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  handleSourceFoundEvent(data) {
    console.log("🔬 Source found:", data);

    // Store source for sidebar rendering
    if (data?.url && data?.title) {
      const sourceId = data.url;
      const source = {
        id: sourceId,
        url: data.url,
        title: data.title,
        description: data.description || "",
        sourceType: data.sourceType || "search_result",
        relevance: data.relevance || 1.0,
        timestamp: new Date().toISOString(),
      };
      this.sources.set(sourceId, source);
      this.stats.totalSources = this.sources.size;
    }

    return {
      status: "active",
      sdk: true,
    };
  }

  handleExtractionCompletedEvent(data) {
    console.log("🔬 Extraction completed:", data);

    const activity = {
      id: `extraction_completed_${Date.now()}_${Math.random()}`,
      type: "extraction",
      status: "completed",
      message:
        data.message ||
        `Extracted ${data.findingsCount || 0} findings from ${data.title || data.url}`,
      timestamp: new Date().toISOString(),
      findingsCount: data.findingsCount,
      relevanceScore: data.relevanceScore,
      extractionMethod: data.extractionMethod,
      url: data.url,
      title: data.title,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    // Recalculate progress
    this.calculateProgress();

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  handleResearchTimeoutEvent(data) {
    console.log("🔬 Research timeout:", data);

    const activity = {
      id: `timeout_${Date.now()}_${Math.random()}`,
      type: "timeout",
      status: "error",
      message:
        data.message ||
        `Research timed out after ${data.timeoutMs || 300000}ms`,
      timestamp: new Date().toISOString(),
      timeoutMs: data.timeoutMs,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    return {
      status: "error",
      isError: true,
      error: data.message || "Research timed out",
      newActivity: activity,
      sdk: true,
    };
  }

  handleSDKEvent(data) {
    console.log("🔬 SDK event:", data);

    const activity = {
      id: `sdk_${Date.now()}_${Math.random()}`,
      type: "sdk_event",
      status: "completed",
      message:
        data.message || `SDK event: ${data.originalEventType || "unknown"}`,
      timestamp: new Date().toISOString(),
      originalEventType: data.originalEventType,
    };

    this.activities.set(activity.id, activity);
    this.stats.totalActivities = this.activities.size;

    return {
      status: "active",
      newActivity: activity,
      sdk: true,
    };
  }

  getSnapshot() {
    return {
      activities: Array.from(this.activities.values()),
      sources: Array.from(this.sources.values()),
      stats: {
        ...this.stats,
        elapsedTime: Date.now() - this.stats.startTime,
      },
      status: "active",
    };
  }

  // Get efficient update for UI
  getUIUpdate() {
    return {
      activitiesCount: this.stats.totalActivities,
      sourcesCount: this.stats.totalSources,
      analyzedSourcesCount: this.stats.analyzedSources,
      elapsedTime: Date.now() - this.stats.startTime,
      progressPercentage: this.stats.progressPercentage,
      estimatedActivities: this.stats.estimatedActivities,
      remainingActivities: this.stats.remainingActivities,
    };
  }
}

const ChatContext = createContext(null);

/**
 * Provider component for chat functionality
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function ChatProvider({ children }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const { userId, currentWorkspaceId, currentWorkspace } = useAuthStore();
  const { currentAssistantId, currentAssistant } = useAssistantStore();

  const {
    currentThreadId,
    currentThread,
    isStreaming,
    messages,
    messageStatus,
    streamStatus,
    currentMessageId,
    setCurrentThread,
    updateMessageState: threadStoreUpdateMessageState,
    isEditedMessageInProgress,
    optimisticMessages,
    getOptimisticMessages,
  } = useThreadStore();

  // OPTIMISTIC RENDERING: Merge optimistic messages with real messages for display
  const messagesWithOptimistic = useMemo(() => {
    if (!currentThreadId) {
      return messages || [];
    }

    const realMessages = messages || [];
    const optimistic = getOptimisticMessages?.(currentThreadId) || [];

    // Filter out optimistic messages that match real messages (by content, role, and ID prefix)
    // This prevents duplication when the real message arrives
    const filteredOptimistic = optimistic.filter((opt) => {
      // Check if there's a real message with the same content and role
      const hasDuplicate = realMessages.some((real) => {
        // Match by role first
        if (real.role !== opt.role) return false;

        // Check if timestamps are very close (within 10 seconds)
        const timeDiff = Math.abs(
          new Date(real.created_at) - new Date(opt.created_at)
        );
        const timeMatch = timeDiff < 10000;

        if (!timeMatch) return false;

        // For exact content match
        const exactContentMatch = real.content === opt.content;
        if (exactContentMatch) return true;

        // For partial content match (when real message starts with optimistic content)
        // This handles cases where the assistant message starts with the same text
        const optContent = (opt.content || "").trim();
        const realContent = (real.content || "").trim();

        // If optimistic content is a prefix of real content (and optimistic is shorter)
        // This catches cases like "I'll search..." appearing in both
        if (optContent.length > 0 && realContent.length > optContent.length) {
          const isPrefix = realContent.startsWith(optContent);
          // Also check if the prefix is substantial (at least 20 chars) to avoid false positives
          if (isPrefix && optContent.length >= 20) {
            return true;
          }
        }

        // If real content is a prefix of optimistic content (optimistic has more)
        if (realContent.length > 0 && optContent.length > realContent.length) {
          const isPrefix = optContent.startsWith(realContent);
          if (isPrefix && realContent.length >= 20) {
            return true;
          }
        }

        return false;
      });

      return !hasDuplicate;
    });

    // Merge and sort by timestamp
    const merged = [...realMessages, ...filteredOptimistic].sort((a, b) => {
      const dateA = new Date(a.created_at || a.timestamp || 0);
      const dateB = new Date(b.created_at || b.timestamp || 0);
      return dateA - dateB;
    });

    return merged;
  }, [currentThreadId, messages, optimisticMessages, getOptimisticMessages]);

  // RACE CONDITION FIX: Create enhanced updateMessageState that handles thread_id parameter
  const updateMessageState = useCallback(
    (msgData) => {
      // Extract thread_id if provided and pass it to the appropriate update method
      const { thread_id: specificThreadId, ...messageData } = msgData;

      if (specificThreadId && msgData.id && !msgData.isStreaming) {
        // Convert to the format expected by updateMessage
        const updates = {
          content: messageData.content,
          role: messageData.role, // CRITICAL: Include role to prevent corruption
          isStreaming: messageData.isStreaming,
          isStreamComplete: messageData.isStreamComplete,
          messageStatus: messageData.messageStatus,
          tool_calls: messageData.tool_calls,
          metadata: messageData.metadata,
          thread_id: specificThreadId, // Pass the locked thread ID
          updated_at: new Date().toISOString(),
        };

        // Use updateMessage for final saves with specific thread ID
        return useThreadStore.getState().updateMessage(msgData.id, updates);
      } else {
        // For streaming updates or when no specific thread ID, use the regular updateMessageState
        return threadStoreUpdateMessageState(msgData);
      }
    },
    [threadStoreUpdateMessageState]
  );

  const enhancedUpdateMessageState = useMemo(
    () => createEnhancedMessageUpdater(updateMessageState),
    [updateMessageState]
  );

  const scrollContainerRef = useRef(null);
  const bottomRef = useRef(null);
  const tokenCounterRef = useRef(null);
  const threadChangeRef = useRef(false);

  // RACE CONDITION FIX: Add ref to capture thread ID at message send time
  // This prevents race conditions where user switches threads while a message is streaming
  // The ref locks the thread ID when the message is sent, ensuring the response streams to the correct thread
  const threadIdRef = useRef(null);

  // RACE CONDITION FIX: Add ref for deep research thread locking
  // This ensures deep research continues streaming to the correct thread even if user navigates away
  const deepResearchThreadIdRef = useRef(null);

  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageContent, setEditingMessageContent] = useState("");
  const [isRenderingCodeBlocks, setIsRenderingCodeBlocks] = useState(false);
  const [preventAutomaticThread, setPreventAutomaticThread] = useState(
    localStorage.getItem("preventAutomaticThread") === "true"
  );
  const [isPremiumDialog, setIsPremiumDialog] = useState(false);
  const [lastThreadId, setLastThreadId] = useState(currentThread?.id);

  const [isDeepResearchEnabled, setIsDeepResearchEnabled] = useState(false);

  // Enhanced Deep Research state - multi-step flow
  const [deepResearchPhase, setDeepResearchPhase] = useState("disabled"); // 'disabled', 'clarifying', 'confirming', 'researching'
  const [originalDeepResearchQuery, setOriginalDeepResearchQuery] =
    useState("");
  const originalDeepResearchQueryRef = useRef(""); // Add ref for persistence across re-renders
  const [clarifyingQuestions, setClarifyingQuestions] = useState("");
  const [userClarifications, setUserClarifications] = useState("");

  // Canvas mode state
  const [isCanvasModeEnabled, setIsCanvasModeEnabled] = useState(false);
  const [pendingCanvasContent, setPendingCanvasContent] = useState(null);

  // CRITICAL FIX: Use ref to persist canvas mode across new-chat-started events
  // This ensures canvas mode survives when new-chat-started fires before handleSendMessage
  const canvasModeEnabledRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    canvasModeEnabledRef.current = isCanvasModeEnabled;
  }, [isCanvasModeEnabled]);

  // Add state to track user message addition
  const [isAddingUserMessage, setIsAddingUserMessage] = useState(false);
  const userMessageAddedRef = useRef(false);

  // Add abort controller state
  const [currentAbortController, setCurrentAbortController] = useState(null);

  // Simplified deep research state - only track active research for sidebar
  const [activeDeepResearch, setActiveDeepResearch] = useState(null);
  const [deepResearchSidebarOpen, setDeepResearchSidebarOpen] = useState(false);

  // Sources sidebar state
  const [sourcesSidebarOpen, setSourcesSidebarOpen] = useState(false);
  const [activeSources, setActiveSources] = useState(null);

  // Image artifact sidebar state
  const [imageArtifactSidebarOpen, setImageArtifactSidebarOpen] =
    useState(false);
  const [activeImageArtifact, setActiveImageArtifact] = useState(null);

  // Tool validation state - tracks enabled tools for current request
  const [currentRequestTools, setCurrentRequestTools] = useState(null);

  // Reply to message state
  const [replyToMessage, setReplyToMessage] = useState(null);

  const startReplyToMessage = useCallback((message) => {
    setReplyToMessage({
      id: message.id,
      role: message.role,
      content: message.content?.substring(0, 150) || "", // Preview
    });
  }, []);

  const cancelReplyToMessage = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  const startEditingMessage = useCallback(
    (messageId, content) => {
      if (isEditingMessage && editingMessageId !== messageId) {
        setIsEditingMessage(false);
        setEditingMessageId(null);
        setEditingMessageContent("");
      }

      setIsEditingMessage(true);
      setEditingMessageId(messageId);
      setEditingMessageContent(content || "");

      if (scrollContainerRef.current) {
        scrollContainerRef.current.dataset.editingMessageId = messageId;
        scrollContainerRef.current.dataset.editingActive = "true";
      }
    },
    [isEditingMessage, editingMessageId]
  );

  const cancelEditingMessage = useCallback(() => {
    setIsEditingMessage(false);
    setEditingMessageId(null);
    setEditingMessageContent("");

    if (scrollContainerRef.current) {
      scrollContainerRef.current.dataset.editingMessageId = "";
      scrollContainerRef.current.dataset.editingActive = "false";
    }
  }, []);

  let saveEditedMessage;

  const toggleDeepResearch = useCallback(() => {
    console.log("🔬 [ChatContext] toggleDeepResearch called");
    setIsDeepResearchEnabled((prev) => {
      const newState = !prev;
      console.log("🔬 [ChatContext] Deep Research state changing:", {
        from: prev,
        to: newState,
      });

      // Reset enhanced deep research state when toggling
      if (!newState) {
        console.log(
          "🔬 [ChatContext] Disabling deep research - resetting all state"
        );
        setDeepResearchPhase("disabled");
        setOriginalDeepResearchQuery("");
        originalDeepResearchQueryRef.current = ""; // Clear ref too
        setClarifyingQuestions("");
        setUserClarifications("");
      } else {
        // When enabling, ensure we start fresh
        console.log("🔬 [ChatContext] Enabling deep research - starting fresh");
        setDeepResearchPhase("disabled");
      }

      return newState;
    });
  }, []);

  // Add a force disable function for more reliable state management
  const forceDisableDeepResearch = useCallback(() => {
    setIsDeepResearchEnabled(false);
    setDeepResearchPhase("disabled");
    setOriginalDeepResearchQuery("");
    originalDeepResearchQueryRef.current = ""; // Clear ref too
    setClarifyingQuestions("");
    setUserClarifications("");
    // Clear active deep research thread ID
    useThreadStore.getState().setActiveDeepResearchThreadId(null);
  }, []);

  // Add function to handle opting out of deep research flow
  const optOutOfDeepResearch = useCallback(() => {
    console.log("🔬 User opted out of deep research flow");
    setIsDeepResearchEnabled(false);
    setDeepResearchPhase("disabled");
    setOriginalDeepResearchQuery("");
    originalDeepResearchQueryRef.current = ""; // Clear ref too
    setClarifyingQuestions("");
    setUserClarifications("");
    // Clear active deep research thread ID
    useThreadStore.getState().setActiveDeepResearchThreadId(null);
  }, []);

  // Helper function to generate clarifying questions
  const generateClarifyingQuestions = useCallback(
    async (query) => {
      try {
        // Get the current model and provider
        const modelStore = useModelStore.getState();
        const currentModel = currentAssistant?.model || "gpt-4.1-mini";
        const providerName = modelStore.getEffectiveProvider();

        // Use backend provider to generate clarifying questions
        const BackendProviderWrapper = (
          await import("@/lib/ai-providers/backend-provider-wrapper")
        ).default;
        const provider = new BackendProviderWrapper(providerName, {
          defaultModel: currentModel,
        });

        const response = await provider.createChatCompletion({
          model: currentModel,
          messages: [
            {
              role: "system",
              content: CLARIFYING_PROMPT,
            },
            {
              role: "user",
              content: query,
            },
          ],
          temperature: 0.7,
          max_tokens: 1000,
        });

        const clarifyingQuestions = response.choices[0]?.message?.content || "";

        return clarifyingQuestions;
      } catch (error) {
        console.error("Error generating clarifying questions:", error);
        throw error;
      }
    },
    [currentAssistant]
  );

  // Helper function to rewrite query with clarifications
  const rewriteQueryWithClarifications = useCallback(
    async (originalQuery, clarifications) => {
      try {
        // Get the current model and provider
        const modelStore = useModelStore.getState();
        const currentModel = currentAssistant?.model || "gpt-4.1-mini";
        const providerName = modelStore.getEffectiveProvider();

        // Use backend provider to rewrite the query
        const BackendProviderWrapper = (
          await import("@/lib/ai-providers/backend-provider-wrapper")
        ).default;
        const provider = new BackendProviderWrapper(providerName, {
          defaultModel: currentModel,
        });

        const combinedInput = `Original query: ${originalQuery}\n\nUser clarifications: ${clarifications}`;

        const response = await provider.createChatCompletion({
          model: currentModel,
          messages: [
            {
              role: "system",
              content: REWRITING_PROMPT,
            },
            {
              role: "user",
              content: combinedInput,
            },
          ],
          temperature: 0.3,
          max_tokens: 1500,
        });

        const rewrittenQuery = response.choices[0]?.message?.content || "";

        return rewrittenQuery;
      } catch (error) {
        console.error("Error rewriting query:", error);
        throw error;
      }
    },
    [currentAssistant]
  );

  // Canvas mode functions
  const toggleCanvasMode = useCallback(() => {
    setIsCanvasModeEnabled((prev) => {
      const newValue = !prev;
      canvasModeEnabledRef.current = newValue; // Update ref immediately
      return newValue;
    });
  }, []);

  const enableCanvasMode = useCallback((content = null) => {
    canvasModeEnabledRef.current = true; // Update ref immediately
    setIsCanvasModeEnabled(true);
    if (content) {
      setPendingCanvasContent(content);
    }
  }, []);

  const disableCanvasMode = useCallback(() => {
    canvasModeEnabledRef.current = false; // Update ref immediately
    setIsCanvasModeEnabled(false);
    setPendingCanvasContent(null);
  }, []);

  // Listen for canvas tool completion events
  useEffect(() => {
    const handleCanvasToolCompleted = (data) => {
      // FIXED: Auto-disable canvas mode after successful canvas tool usage
      // This prevents the feedback loop where canvas mode stays active
      if (data.success) {
        disableCanvasMode();
      }
    };

    const handleCanvasStreamComplete = (data) => {
      // Also disable canvas mode when canvas streaming completes
      if (data.isComplete !== false) {
        disableCanvasMode();
      }
    };

    // Handle clearing deep research state when starting new chat
    const handleClearDeepResearchState = (event) => {
      console.log("🔬 Clearing deep research state for new chat");
      setIsDeepResearchEnabled(false);
      setActiveDeepResearch(null);
      setDeepResearchSidebarOpen(false);
      // Also reset deep research phase and related state
      setDeepResearchPhase("disabled");
      setOriginalDeepResearchQuery("");
      originalDeepResearchQueryRef.current = ""; // Clear ref too
      setClarifyingQuestions("");
      setUserClarifications("");
    };

    // ADDED: Handle new chat started event to reset all tool states
    const handleNewChatStarted = (event) => {
      // Reset deep research state
      setIsDeepResearchEnabled(false);
      setDeepResearchPhase("disabled");
      setOriginalDeepResearchQuery("");
      originalDeepResearchQueryRef.current = ""; // Clear ref too
      setClarifyingQuestions("");
      setUserClarifications("");
      setActiveDeepResearch(null);
      setDeepResearchSidebarOpen(false);

      // Reset canvas mode state
      // CRITICAL FIX: Check ref value AND current state - if canvas mode was enabled, preserve it
      // The ref persists even if state was reset, allowing us to restore it
      // Also check current state as a backup in case ref hasn't been updated yet
      const shouldPreserveCanvasMode =
        event?.detail?.preserveCanvasMode === true;
      const wasCanvasModeEnabledBeforeReset =
        canvasModeEnabledRef.current || isCanvasModeEnabled;

      if (!shouldPreserveCanvasMode && !wasCanvasModeEnabledBeforeReset) {
        setIsCanvasModeEnabled(false);
        setPendingCanvasContent(null);
        canvasModeEnabledRef.current = false;
      } else if (wasCanvasModeEnabledBeforeReset) {
        // Restore canvas mode state - update both state and ref to be safe
        setIsCanvasModeEnabled(true);
        canvasModeEnabledRef.current = true; // Ensure ref is true
      }

      // Reset sidebar states
      setSourcesSidebarOpen(false);
      setActiveSources(null);
      setImageArtifactSidebarOpen(false);
      setActiveImageArtifact(null);
    };

    // Set up event listeners
    window.addEventListener(
      "clear-deep-research-state",
      handleClearDeepResearchState
    );
    window.addEventListener("new-chat-started", handleNewChatStarted);

    // Try to import and listen to canvas events
    (async () => {
      try {
        const { canvasEventEmitter } = await import(
          "@/lib/ai/tools/canvasStreamingTool"
        );
        canvasEventEmitter.on(
          "canvas_tool_completed",
          handleCanvasToolCompleted
        );
        canvasEventEmitter.on(
          "canvas_stream_complete",
          handleCanvasStreamComplete
        );

        return () => {
          canvasEventEmitter.off(
            "canvas_tool_completed",
            handleCanvasToolCompleted
          );
          canvasEventEmitter.off(
            "canvas_stream_complete",
            handleCanvasStreamComplete
          );
        };
      } catch (error) {
        console.error(
          "Could not set up canvas tool completion listener:",
          error
        );
      }
    })();

    // Cleanup function
    return () => {
      window.removeEventListener(
        "clear-deep-research-state",
        handleClearDeepResearchState
      );
      window.removeEventListener("new-chat-started", handleNewChatStarted);
    };
  }, [
    disableCanvasMode,
    setIsDeepResearchEnabled,
    setActiveDeepResearch,
    setDeepResearchSidebarOpen,
  ]);

  // Listen for memory extraction events (for high-importance memories)
  useEffect(() => {
    const handleMemoryExtracted = (event) => {
      const { count, memories } = event.detail || {};
      if (!count || !memories) return;

      // Only show toast for high-importance memories
      const hasHighImportance = memories.some(
        (m) => m.importance === "high"
      );

      if (hasHighImportance) {
        toast({
          title: "Memory saved",
          description: `${count} important detail${count > 1 ? "s" : ""} remembered from this conversation`,
        });
      }
    };

    window.addEventListener("memory-extracted", handleMemoryExtracted);
    return () => {
      window.removeEventListener("memory-extracted", handleMemoryExtracted);
    };
  }, [toast]);

  // Initialize chat scroll hook with direct messages from thread store
  const { scrollToBottom } = useChatScroll({
    containerRef: scrollContainerRef,
    currentThreadId,
    messages: messages || [], // Use messages directly from thread store
  });

  // Initialize stream state helper function
  const initializeStreamState = useCallback(() => {
    return {
      isStreaming: true,
      streamStatus: "started",
      messageStatus: "created",
      isStreamComplete: false,
      toolExecutionActive: false,
      isToolCallStreaming: false,
      isHandlingMessage: true,
      error: null,
      preventNewMessageClear: true,
      streamStartedAt: Date.now(),
      lastStreamActivity: Date.now(),
    };
  }, []);

  // Function to stream clarifying questions for deep research
  const streamClarifyingQuestions = useCallback(
    async (query, threadId) => {
      try {
        const clarifyingMessageId = fileUtils.generateUUID();
        const timestamp = new Date().toISOString();

        // Create initial assistant message for clarifying questions
        const initialMessage = {
          id: clarifyingMessageId,
          message_id: clarifyingMessageId,
          thread_id: threadId,
          workspace_id: currentWorkspaceId,
          role: "assistant",
          content:
            "I'm analyzing your query to ask some clarifying questions...",
          created_at: timestamp,
          updated_at: timestamp,
          provider: "assistant",
          metadata: {
            isDeepResearchClarifying: true,
            deepResearchPhase: "clarifying",
            originalQuery: query,
            isStreaming: true,
            hideActionButtons: true,
            isIntermediateMessage: true,
            isDeepResearchPhase1: true,
          },
        };

        // Add initial message to thread store
        useThreadStore.setState((state) => ({
          ...state,
          messages: [...(state.messages || []), initialMessage],
          preventNewMessageClear: true,
        }));

        // Get the setState function outside of the async context to avoid hook issues
        const threadStoreSetState = useThreadStore.setState;

        // Stream clarifying questions using the existing streaming infrastructure
        await streamProviderChat({
          messages: [
            {
              role: "system",
              content: CLARIFYING_PROMPT,
            },
            {
              role: "user",
              content: query,
            },
          ],
          threadId,
          messageId: clarifyingMessageId,
          assistantId: null,
          providerName: "openai",
          modelName: "gpt-4o",
          tools: [],
          threadStore: threadStoreSetState,
          workspace_id: currentWorkspaceId,
          updateMessageState: (messageUpdate) => {
            threadStoreSetState((state) => ({
              ...state,
              messages: state.messages.map((msg) =>
                msg.id === clarifyingMessageId
                  ? {
                      ...msg,
                      ...messageUpdate,
                      metadata: {
                        // Preserve the crucial deep research metadata
                        isDeepResearchClarifying: true,
                        deepResearchPhase: "clarifying",
                        originalQuery: query,
                        isDeepResearchPhase1: true,
                        // Merge with any new metadata from the update
                        ...msg.metadata,
                        ...messageUpdate.metadata,
                      },
                    }
                  : msg
              ),
              preventNewMessageClear: true,
            }));
          },
          saveToDatabase: async (msgData) => {
            try {
              if (
                msgData.isStreaming === true ||
                msgData.skipDatabaseSave === true
              ) {
                return { success: true, id: msgData.id };
              }

              // Ensure the critical metadata is preserved
              const preservedMetadata = {
                isDeepResearchClarifying: true,
                deepResearchPhase: "clarifying",
                originalQuery: query,
                hideActionButtons: true,
                isIntermediateMessage: true,
                isDeepResearchPhase1: true,
                ...msgData.metadata, // Add any additional metadata from streaming
              };

              console.log(
                "🔬 Saving clarifying questions with metadata:",
                preservedMetadata
              );
              await saveAssistantMessage({
                ...msgData,
                metadata: preservedMetadata,
              });

              // CRITICAL: Also update the thread store with the preserved metadata
              threadStoreSetState((state) => ({
                ...state,
                messages: state.messages.map((msg) =>
                  msg.id === clarifyingMessageId
                    ? {
                        ...msg,
                        metadata: preservedMetadata,
                      }
                    : msg
                ),
                preventNewMessageClear: true,
              }));

              return { success: true, id: msgData.id };
            } catch (error) {
              console.error("Clarifying questions database save error:", error);
              return { success: false, error: error.message };
            }
          },
        });

        // After streaming is complete, update the phase to await clarifications
        setDeepResearchPhase("awaiting_clarifications");
        setClarifyingQuestions(query); // Store the original query

        // CRITICAL: Verify and force-update metadata after streaming completes
        setTimeout(() => {
          const currentMessages = useThreadStore.getState().messages || [];
          const clarifyingMessage = currentMessages.find(
            (msg) => msg.id === clarifyingMessageId
          );

          console.log(
            "🔬 Verifying clarifying message metadata:",
            clarifyingMessage?.metadata
          );

          if (
            clarifyingMessage &&
            !clarifyingMessage.metadata?.isDeepResearchClarifying
          ) {
            console.log("🔬 FIXING: Metadata was overwritten, restoring it...");

            // Force restore the metadata
            useThreadStore.setState((state) => ({
              ...state,
              messages: state.messages.map((msg) =>
                msg.id === clarifyingMessageId
                  ? {
                      ...msg,
                      metadata: {
                        ...msg.metadata,
                        isDeepResearchClarifying: true,
                        deepResearchPhase: "clarifying",
                        originalQuery: query,
                        hideActionButtons: true,
                        isIntermediateMessage: true,
                        isDeepResearchPhase1: true,
                      },
                    }
                  : msg
              ),
              preventNewMessageClear: true,
            }));
          }
        }, 500);

        // Scroll to bottom after clarifying questions are displayed
        setTimeout(() => {
          if (scrollToBottom) {
            scrollToBottom();
          }
        }, 1500);
      } catch (error) {
        console.error("Error streaming clarifying questions:", error);
        toast({
          title: "Deep Research Error",
          description: "Failed to generate clarifying questions.",
          variant: "destructive",
        });

        // Reset state on error
        setDeepResearchPhase("disabled");
        setIsDeepResearchEnabled(false);
      }
    },
    [
      currentWorkspaceId,
      toast,
      scrollToBottom,
      setDeepResearchPhase,
      setClarifyingQuestions,
      setIsDeepResearchEnabled,
    ]
  );

  // Helper function to add user message for Phase 1
  const addUserMessageForPhase1 = useCallback(
    async (content, threadId) => {
      const timestamp = new Date().toISOString();
      const userMessageId = fileUtils.generateUUID();

      const userMsg = {
        id: userMessageId,
        message_id: userMessageId,
        thread_id: threadId,
        workspace_id: currentWorkspaceId,
        role: "user",
        content: content,
        created_at: timestamp,
        updated_at: timestamp,
        metadata: {
          isDeepResearchQuery: true,
          deepResearchPhase: "initial",
          timestamp: timestamp,
        },
      };

      // Add user message directly to thread store
      useThreadStore.setState((state) => ({
        ...state,
        messages: [...(state.messages || []), userMsg],
        preventNewMessageClear: true,
      }));

      return { userMessageId, success: true };
    },
    [currentWorkspaceId]
  );

  const handleDeepResearch = useCallback(
    async (
      messageId,
      query,
      threadId,
      assistantMessageId,
      threadIdRefParam = null
    ) => {
      // Use the passed thread ID directly
      const lockedThreadId = threadId;

      console.log("🔬 [handleDeepResearch] Starting with query:", query);
      console.log("🔬 [handleDeepResearch] Query length:", query?.length);
      console.log(
        "🔬 [handleDeepResearch] First 200 chars:",
        query?.substring(0, 200)
      );

      try {
        // Set active deep research thread ID in thread store
        useThreadStore.getState().setActiveDeepResearchThreadId(lockedThreadId);

        // Initialize the optimized event handler
        const eventHandler = new DeepResearchEventHandler();

        // Initialize the assistant message with deep research metadata
        const initialDeepResearchData = {
          status: "active",
          activity: [],
          sources: [],
          query,
          startTime: eventHandler.stats.startTime,
          activitiesCount: 0,
          sourcesCount: 0,
          elapsedTime: 0,
        };

        // Update the assistant message with initial deep research data
        useThreadStore.setState((state) => ({
          ...state,
          messages: state.messages.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  metadata: {
                    ...msg.metadata,
                    isDeepResearchResult: true,
                    originalQueryMessageId: messageId,
                    deepResearch: initialDeepResearchData,
                    isDeepResearchInProgress: true,
                  },
                }
              : msg
          ),
        }));

        // Import API_BASE_URL dynamically since it was removed from imports
        const { API_BASE_URL } = await import("@/lib/constants");
        const apiUrl = API_BASE_URL + "/api/research/multi-agent-research";

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify({
            query,
            threadId: lockedThreadId,
            maxDepth: 2,
            timeLimit: 600,
            maxUrls: 10,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("🔬 SSE API error response:", errorText);

          useThreadStore.setState((state) => ({
            ...state,
            messages: state.messages.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: `❌ Deep research failed: ${errorText}` }
                : msg
            ),
          }));

          throw new Error(
            `API error: ${response.status} ${response.statusText} - ${errorText}`
          );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // Helper function to update UI efficiently
        const updateDeepResearchUI = async (
          messageId,
          assistantMessageId,
          updates,
          snapshot,
          threadId
        ) => {
          const currentSnapshot = snapshot || eventHandler.getSnapshot();

          // Update the assistant message with current data
          const updatedDeepResearchData = {
            ...initialDeepResearchData,
            activity: currentSnapshot.activities,
            sources: currentSnapshot.sources,
            activitiesCount: currentSnapshot.stats.totalActivities,
            sourcesCount: currentSnapshot.stats.totalSources,
            elapsedTime: currentSnapshot.stats.elapsedTime,
            status: updates.status || currentSnapshot.status,
            estimatedActivities: currentSnapshot.stats.estimatedActivities,
            remainingActivities: currentSnapshot.stats.remainingActivities,
            progressPercentage: currentSnapshot.stats.progressPercentage,
            ...(updates.isComplete && {
              finalAnalysis: updates.finalAnalysis,
              sourceLinks: updates.sourceLinks,
              endTime: currentSnapshot.stats.completedAt,
            }),
            ...(updates.isError && {
              error: updates.error,
              errorAt: currentSnapshot.stats.errorAt,
            }),
          };

          // Always update activeDeepResearch if this is the active research
          if (
            activeDeepResearch &&
            activeDeepResearch.messageId === messageId
          ) {
            setActiveDeepResearch({
              messageId,
              activity: currentSnapshot.activities,
              sources: currentSnapshot.sources,
              status: updates.status || currentSnapshot.status,
              query,
              activitiesCount: currentSnapshot.stats.totalActivities,
              sourcesCount: currentSnapshot.stats.totalSources,
              elapsedTime: currentSnapshot.stats.elapsedTime,
              estimatedActivities: currentSnapshot.stats.estimatedActivities,
              remainingActivities: currentSnapshot.stats.remainingActivities,
              progressPercentage: currentSnapshot.stats.progressPercentage,
              ...(updates.finalAnalysis && {
                finalAnalysis: updates.finalAnalysis,
              }),
            });
          }

          // Auto-open deep research sidebar on first activity - DISABLED
          // Deep research now streams inline via DeepResearchBlock component
          // if (
          //   currentSnapshot.stats.totalActivities === 1 &&
          //   !deepResearchSidebarOpen
          // ) {
          //   setActiveDeepResearch({
          //     messageId,
          //     activity: currentSnapshot.activities,
          //     sources: currentSnapshot.sources,
          //     status: "active",
          //     query,
          //     activitiesCount: currentSnapshot.stats.totalActivities,
          //     sourcesCount: currentSnapshot.stats.totalSources,
          //     elapsedTime: currentSnapshot.stats.elapsedTime,
          //     estimatedActivities: currentSnapshot.stats.estimatedActivities,
          //     remainingActivities: currentSnapshot.stats.remainingActivities,
          //     progressPercentage: currentSnapshot.stats.progressPercentage,
          //   });
          //   setDeepResearchSidebarOpen(true);
          // }

          // Dispatch live update event for DeepResearch sidebar
          try {
            const deepResearchUpdateEvent = new CustomEvent(
              "deepResearchUpdate",
              {
                detail: {
                  messageId,
                  data: {
                    type: updates.isComplete
                      ? "complete"
                      : updates.isError
                        ? "error"
                        : "activity",
                    activity: currentSnapshot.activities,
                    sources: currentSnapshot.sources,
                    status: updates.status || currentSnapshot.status,
                    activitiesCount: currentSnapshot.stats.totalActivities,
                    sourcesCount: currentSnapshot.stats.totalSources,
                    elapsedTime: currentSnapshot.stats.elapsedTime,
                    estimatedActivities:
                      currentSnapshot.stats.estimatedActivities,
                    remainingActivities:
                      currentSnapshot.stats.remainingActivities,
                    progressPercentage:
                      currentSnapshot.stats.progressPercentage,
                    ...(updates.newActivity && {
                      newActivity: updates.newActivity,
                    }),
                    ...(updates.finalAnalysis && {
                      finalAnalysis: updates.finalAnalysis,
                    }),
                    ...(updates.error && { error: updates.error }),
                  },
                },
              }
            );
            window.dispatchEvent(deepResearchUpdateEvent);
          } catch (eventError) {
            console.warn(
              "Could not dispatch deep research update event:",
              eventError
            );
          }

          // Update thread store with optimized data
          useThreadStore.setState((state) => {
            const foundMessage = state.messages.find(
              (msg) => msg.id === assistantMessageId
            );

            if (!foundMessage) {
              const newAssistantMessage = {
                id: assistantMessageId,
                message_id: assistantMessageId,
                thread_id: threadId,
                role: "assistant",
                content: updates.finalAnalysis || "",
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                metadata: {
                  isDeepResearchInProgress:
                    !updates.isComplete && !updates.isError,
                  deepResearch: updatedDeepResearchData,
                  deepResearchStable: true,
                  progressUpdateId: `${assistantMessageId}-${currentSnapshot.stats.totalActivities}-${currentSnapshot.stats.totalSources}`,
                  ...(updates.newActivity && {
                    currentActivity: updates.newActivity.message,
                  }),
                  ...(updates.isComplete && {
                    isComplete: true,
                    completedAt: new Date().toISOString(),
                    deepResearchComplete: true,
                  }),
                  ...(updates.isError && {
                    isError: true,
                    errorAt: new Date().toISOString(),
                  }),
                },
              };

              return {
                ...state,
                messages: [...state.messages, newAssistantMessage],
                preventNewMessageClear: true,
              };
            }

            const updatedMessages = state.messages.map((msg) => {
              if (msg.id === assistantMessageId) {
                return {
                  ...msg,
                  content: updates.finalAnalysis || msg.content,
                  thread_id: threadId,
                  updated_at: new Date().toISOString(),
                  metadata: {
                    ...msg.metadata,
                    isDeepResearchInProgress:
                      !updates.isComplete && !updates.isError,
                    deepResearch: updatedDeepResearchData,
                    deepResearchStable: true,
                    progressUpdateId: `${assistantMessageId}-${currentSnapshot.stats.totalActivities}-${currentSnapshot.stats.totalSources}`,
                    ...(updates.newActivity && {
                      currentActivity: updates.newActivity.message,
                    }),
                    ...(updates.isComplete && {
                      isComplete: true,
                      completedAt: new Date().toISOString(),
                      deepResearchComplete: true,
                      isDeepResearchInProgress: false,
                      isDeepResearchFinalReport: true,
                    }),
                    ...(updates.isError && {
                      isError: true,
                      errorAt: new Date().toISOString(),
                    }),
                  },
                };
              }
              return msg;
            });

            return {
              ...state,
              messages: updatedMessages,
              preventNewMessageClear: true,
            };
          });
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");

          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const jsonString = line.slice(6);
                const data = JSON.parse(jsonString);

                console.log("🔬 Received SSE event:", data);

                const updates = eventHandler.handleEvent(data);

                console.log("🔬 Event handler result:", updates);

                if (updates) {
                  await updateDeepResearchUI(
                    messageId,
                    assistantMessageId,
                    updates,
                    eventHandler.getSnapshot(),
                    lockedThreadId
                  );

                  if (updates.isComplete) {
                    const snapshot = eventHandler.getSnapshot();

                    try {
                      const { supabase } = await import("@/lib/supabase");

                      const assistantMessageData = {
                        id: assistantMessageId,
                        message_id: assistantMessageId,
                        thread_id: lockedThreadId,
                        workspace_id: currentWorkspaceId,
                        role: "assistant",
                        content: updates.finalAnalysis,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        provider: "assistant",
                        metadata: {
                          isDeepResearchResult: true,
                          isDeepResearchFinalReport: true,
                          originalQueryMessageId: messageId,
                          isComplete: true,
                          completedAt: new Date().toISOString(),
                          deepResearch: {
                            status: "complete",
                            finalAnalysis: updates.finalAnalysis,
                            activity: snapshot.activities,
                            sources: snapshot.sources,
                            sourceLinks: updates.sourceLinks,
                            activitiesCount: snapshot.stats.totalActivities,
                            sourcesCount: snapshot.stats.totalSources,
                            elapsedTime: snapshot.stats.elapsedTime,
                            endTime: snapshot.stats.completedAt,
                            query: query,
                            startTime: eventHandler.stats.startTime,
                          },
                        },
                      };

                      const { error } = await supabase
                        .from("thread_messages")
                        .upsert([assistantMessageData], {
                          onConflict: "id",
                          ignoreDuplicates: false,
                        })
                        .select();

                      if (error) {
                        console.error(
                          "Error upserting deep research result:",
                          error
                        );
                      }

                      // Update user message metadata
                      await supabase
                        .from("thread_messages")
                        .update({
                          metadata: {
                            ...((
                              await supabase
                                .from("thread_messages")
                                .select("metadata")
                                .eq("id", messageId)
                                .single()
                            )?.data?.metadata || {}),
                            hasDeepResearch: true,
                            deepResearchAssistantMessageId: assistantMessageId,
                            deepResearch: {
                              status: "complete",
                              query: query,
                              startTime: eventHandler.stats.startTime,
                              endTime: snapshot.stats.completedAt,
                              elapsedTime: snapshot.stats.elapsedTime,
                            },
                          },
                        })
                        .eq("id", messageId);

                      // Handle navigation (title already generated in Phase 2 for enhanced deep research)
                      setTimeout(async () => {
                        try {
                          // Check if this is an enhanced deep research (with clarifications)
                          const currentThread =
                            useThreadStore.getState().currentThread;
                          const hasExistingTitle =
                            currentThread?.title &&
                            currentThread.title !== "New Chat" &&
                            currentThread.title !== "New Thread" &&
                            currentThread.title !== "Untitled Thread";

                          // Only generate title if we don't have one yet (fallback for single-phase deep research)
                          if (!hasExistingTitle && lockedThreadId) {
                            const userMessage = useThreadStore
                              .getState()
                              .messages?.find(
                                (m) => m.id === messageId && m.role === "user"
                              );

                            const titleContent = userMessage?.content || query;

                            if (titleContent) {
                              const { generateTitleImmediately } =
                                useThreadStore.getState();
                              await generateTitleImmediately(
                                lockedThreadId,
                                titleContent
                              );
                              console.log(
                                "🔬 Generated fallback title for single-phase deep research"
                              );
                            }
                          } else {
                            console.log(
                              "🔬 Skipping title generation - already have enhanced title from Phase 2"
                            );
                          }
                        } catch (titleError) {
                          console.error(
                            "Error in title handling after deep research completion:",
                            titleError
                          );
                        }
                      }, 500);

                      setTimeout(async () => {
                        try {
                          // CRITICAL FIX: Don't navigate if user is on /chat/new (background deep research completed)
                          const currentPath = window.location.pathname;
                          if (
                            lockedThreadId &&
                            currentPath !== `/chat/${lockedThreadId}` &&
                            currentPath !== "/chat/new"
                          ) {
                            navigate(`/chat/${lockedThreadId}`, {
                              replace: true,
                            });
                          }

                          setTimeout(() => {
                            if (scrollToBottom) {
                              scrollToBottom();
                            }
                          }, 100);
                        } catch (navigationError) {
                          console.error(
                            "Error with navigation after deep research:",
                            navigationError
                          );
                        }
                      }, 1000);

                      setTimeout(() => {
                        if (scrollToBottom) {
                          scrollToBottom();
                        }
                      }, 1500);

                      setTimeout(() => {
                        setIsDeepResearchEnabled(false);
                        setDeepResearchPhase("disabled");
                        setOriginalDeepResearchQuery("");
                        setClarifyingQuestions("");
                        setUserClarifications("");
                        // Clear active deep research thread ID
                        useThreadStore
                          .getState()
                          .setActiveDeepResearchThreadId(null);
                      }, 2000);
                    } catch (dbError) {
                      console.error(
                        "Error saving deep research result:",
                        dbError
                      );
                    }
                  }

                  if (updates.isError) {
                    setTimeout(() => {
                      if (scrollToBottom) {
                        scrollToBottom();
                      }
                    }, 300);
                    throw new Error(updates.error);
                  }
                }
              } catch (parseError) {
                console.warn("🔬 JSON parse error:", parseError.message);
                continue;
              }
            }
          }
        }
      } catch (error) {
        console.error("🔬 Deep research error:", error);

        useThreadStore.setState((state) => ({
          ...state,
          messages: state.messages.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content: `❌ Deep research failed: ${error.message}`,
                  thread_id: lockedThreadId,
                  metadata: {
                    ...msg.metadata,
                    deepResearch: {
                      status: "error",
                      error: error.message,
                      query,
                      activity: [],
                      sources: [],
                      activitiesCount: 0,
                      sourcesCount: 0,
                      elapsedTime: 0,
                    },
                  },
                }
              : msg
          ),
        }));

        toast({
          title: "Deep Research Error",
          description: error.message,
          variant: "destructive",
        });

        setTimeout(() => {
          if (scrollToBottom) {
            scrollToBottom();
          }
        }, 500);

        setTimeout(() => {
          setIsDeepResearchEnabled(false);
          setDeepResearchPhase("disabled");
          setOriginalDeepResearchQuery("");
          setClarifyingQuestions("");
          setUserClarifications("");
          // Clear active deep research thread ID on error
          useThreadStore.getState().setActiveDeepResearchThreadId(null);
        }, 1000);
      } finally {
        if (deepResearchThreadIdRef.current === lockedThreadId) {
          deepResearchThreadIdRef.current = null;
        }
      }
    },
    [toast, deepResearchSidebarOpen, setActiveDeepResearch, navigate]
  );

  const messagesRef = useRef([]);
  const backedUpMessagesRef = useRef([]);

  useEffect(() => {
    if (messages && Array.isArray(messages) && messages.length > 0) {
      messagesRef.current = [...messages];
    }
  }, [messages]);

  const preserveMessagesBeforeSend = useCallback(() => {
    try {
      const currentMessages = useThreadStore.getState().messages || [];

      if (
        currentMessages &&
        Array.isArray(currentMessages) &&
        currentMessages.length > 0
      ) {
        backedUpMessagesRef.current = [];

        const messagesWithThreadId = currentMessages.map((msg) => ({
          ...msg,
          thread_id: currentThreadId || msg.thread_id,
          threadId: currentThreadId || msg.threadId,
        }));

        backedUpMessagesRef.current = messagesWithThreadId;

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error preserving messages:", error);
      return false;
    }
  }, [currentThreadId]);

  /**
   * Fast thread creation with minimal required data for immediate UI feedback
   */
  const createThreadOptimistic = useCallback(async (basicData) => {
    try {
      const { userId, currentWorkspaceId } = useAuthStore.getState();

      // OPTIMISTIC RENDERING FIX: Use provided thread_id if available
      const minimalThreadData = {
        user_id: userId,
        workspace_id: basicData.workspace_id || currentWorkspaceId,
        title: "New Chat",
        metadata: {
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_active: true,
          needs_title_generation: true,
          ...basicData.metadata,
        },
        // CRITICAL FIX: Include optimistic thread ID if provided
        ...(basicData.thread_id && { id: basicData.thread_id }),
      };

      // Create thread in database with minimal data
      const newThread = await useThreadStore.getState().createThread(
        userId,
        null, // assistantId
        "New Chat", // title
        "", // initialContext
        minimalThreadData
      );

      if (!newThread?.id) {
        throw new Error("Failed to create thread");
      }

      // Immediately update local state
      useThreadStore.setState({
        currentThread: newThread,
        currentThreadId: newThread.id,
        currentThreadTitle: newThread.title,
        messages: [],
        preventNewMessageClear: true,
      });

      // Enhance thread with additional data in background (non-blocking)
      setTimeout(() => {
        enhanceThreadAsync(newThread.id, basicData);
      }, 0);

      return newThread;
    } catch (error) {
      console.error("Fast thread creation failed:", error);
      throw error;
    }
  }, []);

  /**
   * Background thread enhancement with vector store, workspace associations, etc.
   */
  const enhanceThreadAsync = useCallback(async (threadId, additionalData) => {
    try {
      const updates = {};

      // Create vector store if needed
      if (additionalData.needsVectorStore !== false) {
        try {
          const threadName = `Thread ${Date.now()}`;
          const vector_store_id = await createVectorStore(threadName);

          if (vector_store_id) {
            updates.vector_store_id = vector_store_id;
            // SIMPLIFIED: Update thread store with vector store ID
            try {
              const { useThreadStore } = await import("@/store/threadStore");
              const threadStore = useThreadStore.getState();
              threadStore.setThreadVectorStore(threadId, vector_store_id);
            } catch (storeError) {
              console.error(
                "Error updating thread store with vector store:",
                storeError
              );
            }
          }
        } catch (vectorStoreError) {
          console.error(
            "Background vector store creation failed:",
            vectorStoreError
          );
        }
      }

      // Add workspace associations if needed
      if (additionalData.isWorkspaceChat && additionalData.workspace_id) {
        try {
          const { SupabaseWorkspaceUtils } = await import(
            "@/lib/db/utils/supabase/workspace"
          );
          await SupabaseWorkspaceUtils.addThreadToWorkspace(
            additionalData.workspace_id,
            threadId
          );
        } catch (workspaceError) {
          console.error(
            "Background workspace association failed:",
            workspaceError
          );
        }
      }

      // Update thread with enhanced data if we have any updates
      if (Object.keys(updates).length > 0) {
        const { supabase } = await import("@/lib/supabase");
        await supabase
          .from("threads")
          .update({
            ...updates,
            metadata: {
              ...additionalData.metadata,
              ...updates,
              enhanced_at: new Date().toISOString(),
            },
          })
          .eq("id", threadId);
      }
    } catch (error) {
      console.error("Background thread enhancement failed:", error);
    }
  }, []);

  const createUserMessageWithValidatedFiles = useCallback(
    async ({
      userMessageId,
      threadId,
      content,
      timestamp,
      currentWorkspaceId,
      storageFiles,
      filePathsWithOpenAIIds,
      messageContext,
      visionImageUrls,
      imageFiles,
      pdfFiles,
      pdfFileIds,
    }) => {
      try {
        const finalStorageFiles = storageFiles || [];
        const finalFilePathsWithOpenAIIds = filePathsWithOpenAIIds || [];

        let storageFilesMetadata = null;

        if (
          finalFilePathsWithOpenAIIds.length > 0 ||
          finalStorageFiles.length > 0
        ) {
          const sourceFiles =
            finalFilePathsWithOpenAIIds.length > 0
              ? finalFilePathsWithOpenAIIds
              : finalStorageFiles;

          // CRITICAL: Preserve ALL file metadata fields for proper display and download
          const fileData = sourceFiles.map((f) => ({
            file_id: f.openai_file_id || f.file_id || f.id,
            openai_file_id: f.openai_file_id || f.file_id || f.id,
            name: f.name || f.path?.split("/").pop() || "file",
            type: f.type || "unknown",
            isImage: f.isImage || f.type?.startsWith("image/") || false,
            isPdf: f.isPdf || f.type === "application/pdf" || false,
            isDocument:
              f.isDocument ||
              (f.type &&
                (f.type.includes("doc") || f.type.includes("text"))) ||
              false,
            isGoogleDrive: f.isGoogleDrive || false,
            supabase_file_id: f.supabase_file_id || f.id,
            path: f.path || f.storage_path,
            storage_path: f.storage_path || f.path,
            publicUrl: f.publicUrl || f.url,
            url: f.url || f.publicUrl,
            size: f.size || 0,
            visionMetadata: f.visionMetadata || null,
            vector_store_attached: f.vector_store_attached || false,
            vector_store_id: f.vector_store_id || null,
            vector_store_file_id: f.vector_store_file_id || f.openai_file_id,
          }));

          storageFilesMetadata = JSON.stringify(fileData);
        }

        const attachmentsCopy = finalFilePathsWithOpenAIIds.map((f) => ({
          id: f.file_id || f.openai_file_id || f.id,
          name: f.name || f.path?.split("/").pop() || "file",
          type: f.type,
          path: f.path || f.storage_path,
          openai_file_id: f.openai_file_id || f.file_id,
        }));

        // CRITICAL FIX: Ensure timestamp is always a valid ISO string
        const validTimestamp = timestamp || new Date().toISOString();

        const userMsg = {
          id: userMessageId,
          message_id: userMessageId,
          thread_id: threadId,
          threadId: threadId,
          workspace_id: currentWorkspaceId, // This is OK - it's for user message creation context
          role: "user", // CRITICAL: Explicitly set role to "user"
          content: content,
          created_at: validTimestamp, // CRITICAL: Always set created_at
          updated_at: validTimestamp, // CRITICAL: Always set updated_at
          timestamp: validTimestamp, // Keep timestamp for compatibility
          attachments: attachmentsCopy,
          metadata: {
            timestamp: validTimestamp,

            ...(messageContext?.vector_store_id && {
              vector_store_id: messageContext.vector_store_id,
            }),

            ...(storageFilesMetadata && {
              storage_files: storageFilesMetadata,
              has_files: true,
              file_count:
                finalFilePathsWithOpenAIIds.length || finalStorageFiles.length,
            }),

            ...(imageFiles.length > 0 && {
              has_images: true,
              image_count: imageFiles.length,
              vision_image_urls: visionImageUrls,
            }),

            ...(pdfFiles.length > 0 && {
              has_pdfs: true,
              pdf_count: pdfFiles.length,
              pdf_file_ids: pdfFileIds,
            }),

            ...(messageContext?.from_workspace_chat && {
              from_workspace_chat: true,
            }),

            // Reply to message metadata
            ...(messageContext?.reply_to && {
              reply_to: messageContext.reply_to,
            }),

            ...(messageContext?.reply_context && {
              reply_context: messageContext.reply_context,
            }),

            // Store image edit context for displaying thumbnail in message
            ...(messageContext?.forceImageEditTool === true &&
              messageContext?.imageEditData && {
                isImageEditRequest: true,
                imageEditContext: {
                  originalImageUrl: messageContext.imageEditData.imageUrls?.[0],
                  maskUrl: messageContext.imageEditData.maskUrl,
                  editPrompt: messageContext.imageEditData.prompt || content,
                },
              }),
          },
        };

        return userMsg;
      } catch (error) {
        console.error(
          "Error creating user message with validated files:",
          error
        );

        // CRITICAL FIX: Ensure fallback message also has proper timestamps
        const fallbackTimestamp = timestamp || new Date().toISOString();

        return {
          id: userMessageId,
          message_id: userMessageId,
          thread_id: threadId,
          threadId: threadId,
          workspace_id: currentWorkspaceId,
          role: "user",
          content: content,
          created_at: fallbackTimestamp, // CRITICAL: Always set created_at
          updated_at: fallbackTimestamp, // CRITICAL: Always set updated_at
          timestamp: fallbackTimestamp, // Keep timestamp for compatibility
          attachments: [],
          metadata: {
            timestamp: fallbackTimestamp,
            error_during_creation: error.message,
          },
        };
      }
    },
    []
  );

  const validateUserMessageIntegrity = useCallback((userMsg) => {
    const validation = {
      isValid: true,
      issues: [],
    };

    try {
      if (!userMsg.id) {
        validation.isValid = false;
        validation.issues.push("Missing message ID");
      }

      if (!userMsg.thread_id) {
        validation.isValid = false;
        validation.issues.push("Missing thread_id");
      }

      if (!userMsg.content) {
        validation.isValid = false;
        validation.issues.push("Missing content");
      }

      const hasAttachments =
        userMsg.attachments && userMsg.attachments.length > 0;
      const claimsToHaveFiles = userMsg.metadata?.has_files === true;
      const hasStorageFiles = !!userMsg.metadata?.storage_files;
      const declaredFileCount = parseInt(
        userMsg.metadata?.file_count || "0",
        10
      );

      if (hasAttachments && !claimsToHaveFiles) {
        validation.isValid = false;
        validation.issues.push(
          "Has attachments but metadata does not indicate files"
        );
      }

      if (claimsToHaveFiles && !hasAttachments && !hasStorageFiles) {
        validation.isValid = false;
        validation.issues.push(
          "Claims to have files but no attachments or storage_files found"
        );
      }

      if (hasAttachments && declaredFileCount !== userMsg.attachments.length) {
        validation.isValid = false;
        validation.issues.push(
          `File count mismatch: attachments=${userMsg.attachments.length}, declared=${declaredFileCount}`
        );
      }

      if (hasStorageFiles) {
        try {
          const parsed = JSON.parse(userMsg.metadata.storage_files);
          if (!Array.isArray(parsed)) {
            validation.isValid = false;
            validation.issues.push("storage_files is not an array");
          }
        } catch (e) {
          validation.isValid = false;
          validation.issues.push("storage_files is not valid JSON");
        }
      }

      return validation;
    } catch (error) {
      console.error("Error validating user message:", error);
      return {
        isValid: false,
        issues: ["Validation error: " + error.message],
      };
    }
  }, []);

  const repairUserMessage = useCallback(
    (userMsg, issues, storageFiles, filePathsWithOpenAIIds) => {
      try {
        if (!userMsg.metadata) {
          userMsg.metadata = {};
        }

        if (userMsg.attachments && userMsg.attachments.length > 0) {
          userMsg.metadata.has_files = true;
          userMsg.metadata.file_count = userMsg.attachments.length;
        }

        if (
          userMsg.attachments &&
          userMsg.attachments.length > 0 &&
          !userMsg.metadata.storage_files
        ) {
          const storageFilesData = userMsg.attachments.map((f) => ({
            path: f.path || f.storage_path,
            file_id: f.openai_file_id || f.file_id || f.id,
            name: f.name || "file",
            type: f.type || "unknown",
            isImage: f.isImage || f.type?.startsWith("image/") || false,
            isPdf: f.isPdf || f.type === "application/pdf" || false,
          }));
          userMsg.metadata.storage_files = JSON.stringify(storageFilesData);
        }

        if (
          userMsg.attachments &&
          userMsg.metadata.file_count !== userMsg.attachments.length
        ) {
          userMsg.metadata.file_count = userMsg.attachments.length;
        }

        if (
          userMsg.metadata.has_files &&
          (!userMsg.attachments || userMsg.attachments.length === 0) &&
          !userMsg.metadata.storage_files
        ) {
          userMsg.metadata.has_files = false;
          userMsg.metadata.file_count = 0;
        }

        userMsg.metadata.repaired_at = Date.now();
        userMsg.metadata.repair_issues = issues;
      } catch (error) {
        console.error("Error during user message repair:", error);
      }
    },
    []
  );

  const handleSendMessage = useCallback(
    async ({
      content,
      files,
      storageFiles,
      contextData,
      fileMetadata,
      metadata,
      isDeepResearch,
      enableDeepResearch,
    }) => {
      if (!content?.trim() && !files?.length) return;

      // CRITICAL FIX: Prevent duplicate message submissions while streaming
      // Check if we're already streaming - if so, don't start another stream
      const threadStoreState = useThreadStore.getState();
      if (threadStoreState.isStreaming) {
        console.warn(
          "[handleSendMessage] Rejected duplicate submission - already streaming"
        );
        return;
      }

      // CRITICAL FIX: Capture canvas mode state IMMEDIATELY at function start
      // Use ref value which persists across state resets, fallback to current state
      // This prevents it from being lost if new-chat-started event resets it
      const capturedCanvasModeEnabled =
        canvasModeEnabledRef.current || isCanvasModeEnabled;

      // CRITICAL FIX: Prevent duplicate user messages during edit operations and reruns
      if (
        contextData?.isResponseToEdit === true ||
        metadata?.isResponseToEdit === true
      ) {
        console.warn(
          "Attempted to create user message during edit operation - this should not happen"
        );
        return;
      }

      if (
        contextData?.isResponseToRerun === true ||
        metadata?.isResponseToRerun === true ||
        contextData?.isRerunMessage === true ||
        metadata?.isRerunMessage === true
      ) {
        console.warn(
          "Attempted to create user message during rerun operation - this should not happen"
        );
        return;
      }

      // CRITICAL FIX: Define combinedContextData early to avoid reference errors
      const combinedContextData = {
        ...(metadata || {}),
        ...(contextData || {}),
      };

      // CRITICAL FIX: Define currentThreadId early to prevent reference errors
      const currentThreadId = currentThread?.id;

      // OPTIMISTIC RENDERING: Create and display user message immediately for perceived performance
      // Generate IDs immediately for optimistic rendering
      const timestamp = new Date().toISOString();
      const optimisticUserMessageId = `temp-${fileUtils.generateUUID()}`;
      const optimisticThreadId = currentThreadId || fileUtils.generateUUID();
      const isNewThread = !currentThreadId;

      // Get current provider and model for proper icon display
      const currentModel = currentAssistant?.model || "gpt-4.1-mini";
      const modelStore = useModelStore.getState();
      const providerName = modelStore.getEffectiveProvider?.() || "openai";

      // Create optimistic user message
      const optimisticUserMessage = {
        id: optimisticUserMessageId,
        role: "user",
        content: content,
        created_at: timestamp,
        updated_at: timestamp,
        thread_id: optimisticThreadId,
        isOptimistic: true,
        metadata: {
          isOptimistic: true,
          tempId: optimisticUserMessageId,
          provider: providerName, // Include provider for correct icon
          model: currentModel, // Include model info
        },
      };

      // Add optimistic message to store immediately
      const threadStore = useThreadStore.getState();
      threadStore.addOptimisticMessage(
        optimisticThreadId,
        optimisticUserMessage
      );

      // Scroll to bottom so user input stays visible
      if (scrollToBottom) {
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      }

      // Navigate immediately if new thread (from /chat/new)
      if (isNewThread && location.pathname === "/chat/new") {
        console.log(
          `[Optimistic Rendering] Navigating to new thread ${optimisticThreadId} immediately`
        );

        // CRITICAL FIX: Add optimistic thread to threads array BEFORE navigating
        // This prevents ChatInterface from showing "chat not found" error
        // when it tries to fetchThread before the actual DB thread is created
        const { userId, currentWorkspaceId } = useAuthStore.getState();
        const optimisticThread = {
          id: optimisticThreadId,
          user_id: userId,
          workspace_id: currentWorkspaceId,
          title: "New Chat",
          created_at: timestamp,
          updated_at: timestamp,
          metadata: {
            isOptimistic: true, // Mark as optimistic so it can be replaced later
            created_at: timestamp,
          },
        };

        // Add the optimistic thread to the threads array
        const currentThreads = threadStore.threads || [];
        threadStore.setThreads([...currentThreads, optimisticThread]);

        // Also set as current thread to prevent race conditions
        threadStore.setCurrentThread(optimisticThread);

        navigate(`/chat/${optimisticThreadId}`, { replace: false });
        threadStore.setCurrentThreadId(optimisticThreadId);
      }

      // SIMPLIFIED: Check deep research state with immediate state check
      // Get the most current state from the store in case React state hasn't updated yet
      const currentDeepResearchState = isDeepResearchEnabled;

      // Check if deep research was just enabled (handles React state update delay)
      const wasJustEnabled = window.__deepResearchJustEnabled === true;

      const deepResearchEnabled =
        currentDeepResearchState ||
        wasJustEnabled ||
        isDeepResearch ||
        enableDeepResearch ||
        contextData?.enable_deep_research === "true" ||
        contextData?.is_deep_research === true ||
        contextData?.deep_research_enabled === "true" ||
        metadata?.enable_deep_research === "true" ||
        metadata?.is_deep_research === true ||
        metadata?.deep_research_enabled === "true";

      console.log("🔬 [ChatContext sendMessage] Deep Research Detection:", {
        currentDeepResearchState,
        wasJustEnabled,
        isDeepResearch,
        enableDeepResearch,
        contextData_enable_deep_research: contextData?.enable_deep_research,
        contextData_is_deep_research: contextData?.is_deep_research,
        deepResearchEnabled,
        isDeepResearchEnabled,
      });

      // Auto-disable deep research after sending message (for non-deep research messages)
      // CRITICAL FIX: Don't auto-disable during multi-phase deep research flow
      if (isDeepResearchEnabled && !deepResearchEnabled) {
        // Check if we're in an active deep research phase - if so, don't auto-disable
        const isInActiveDeepResearchFlow =
          deepResearchPhase === "clarifying" ||
          deepResearchPhase === "awaiting_clarifications" ||
          deepResearchPhase === "confirming" ||
          deepResearchPhase === "researching";

        if (!isInActiveDeepResearchFlow) {
          setIsDeepResearchEnabled(false);
        } else {
          console.log(
            "🔬 Keeping deep research enabled - in active deep research flow phase:",
            deepResearchPhase
          );
        }
      }

      // Enhanced Deep Research Flow with clarifying questions
      if (deepResearchEnabled) {
        // Clear any stale thread references when starting deep research from /chat/new
        if (location.pathname === "/chat/new") {
          // Clear thread refs to ensure clean state
          threadIdRef.current = null;
          deepResearchThreadIdRef.current = null;
        }

        // Check if the last assistant message has clarification metadata
        const currentMessages = useThreadStore.getState().messages || [];
        const lastAssistantMessage = currentMessages
          .filter((m) => m.role === "assistant")
          .pop();

        const hasAskedClarifyingQuestions =
          lastAssistantMessage?.metadata?.isDeepResearchClarifying === true;

        // Add debugging logs
        console.log("🔬 Deep Research Phase Detection:", {
          deepResearchPhase,
          hasAskedClarifyingQuestions,
          lastAssistantMessage: lastAssistantMessage?.metadata,
          totalMessages: currentMessages.length,
          assistantMessages: currentMessages.filter(
            (m) => m.role === "assistant"
          ).length,
        });

        // Phase 2: User provided clarifications - confirm understanding and start research
        // CHECK THIS FIRST to prevent Phase 1 from intercepting clarification responses
        if (
          deepResearchPhase === "awaiting_clarifications" ||
          hasAskedClarifyingQuestions
        ) {
          console.log("🔬 Entering Phase 2: Processing clarifications");

          // Check if user wants to opt out of deep research and proceed with regular chat
          const optOutKeywords = [
            "proceed with original",
            "original query",
            "skip deep research",
            "no deep research",
            "regular response",
            "normal response",
            "just answer",
            "proceed as-is",
            "skip research",
            "no research",
          ];

          const userWantsOptOut = optOutKeywords.some((keyword) =>
            content.toLowerCase().includes(keyword.toLowerCase())
          );

          if (userWantsOptOut) {
            console.log(
              "🔬 User opted out of deep research, proceeding with regular response"
            );
            optOutOfDeepResearch();

            // Continue with regular chat flow using the original query
            const originalQuery = originalDeepResearchQuery || content;

            // Remove the clarifying questions message from the thread
            const currentMessages = useThreadStore.getState().messages || [];
            const filteredMessages = currentMessages.filter(
              (msg) =>
                !msg.metadata?.isDeepResearchClarifying &&
                !msg.metadata?.isDeepResearchPhase1
            );

            useThreadStore.setState({
              messages: filteredMessages,
              preventNewMessageClear: true,
            });

            // CRITICAL FIX: Replace the current message content with the original query
            // This ensures we process the original query instead of the "no clarification" message
            content = originalQuery;

            // Fall through to regular chat handling with the original query
            // Don't return here - let it continue to regular message processing
          } else {
            try {
              // Store user clarifications
              setUserClarifications(content);
              setDeepResearchPhase("confirming");

              // Automatically disable deep research to prevent accidental re-triggering
              // Users typically don't want multiple deep research sessions in a row
              // This also updates the UI to show deep research as untoggled
              setIsDeepResearchEnabled(false);

              // Use the current thread ID for Phase 2
              const validThreadId = currentThread?.id;

              if (!validThreadId) {
                throw new Error("Thread not found for clarifications");
              }

              // Lock the thread ID
              threadIdRef.current = validThreadId;
              deepResearchThreadIdRef.current = validThreadId;

              // Ensure active deep research thread ID is set for Phase 2
              useThreadStore
                .getState()
                .setActiveDeepResearchThreadId(validThreadId);

              // Add user clarification message to thread using existing function
              const timestamp = new Date().toISOString();
              const userMessageId = fileUtils.generateUUID();

              const userMsg = {
                id: userMessageId,
                message_id: userMessageId,
                thread_id: validThreadId,
                workspace_id: currentWorkspaceId,
                role: "user",
                content: content,
                created_at: timestamp,
                updated_at: timestamp,
                metadata: {
                  isDeepResearchClarification: true,
                  deepResearchPhase: "clarifications",
                  originalQuery: originalDeepResearchQuery,
                  timestamp: timestamp,
                },
              };

              // Add user message directly to thread store
              useThreadStore.setState((state) => ({
                ...state,
                messages: [...(state.messages || []), userMsg],
                preventNewMessageClear: true,
              }));

              // CRITICAL FIX: Save the clarification user message to database
              try {
                const { supabase } = await import("@/lib/supabase");

                const dbUserMsg = {
                  id: userMessageId,
                  message_id: userMessageId,
                  thread_id: validThreadId,
                  workspace_id: currentWorkspaceId,
                  role: "user",
                  content: content,
                  created_at: timestamp,
                  updated_at: timestamp,
                  provider: "user",
                  metadata: {
                    isDeepResearchClarification: true,
                    deepResearchPhase: "clarifications",
                    originalQuery: originalDeepResearchQuery,
                    timestamp: timestamp,
                  },
                };

                const { error } = await supabase
                  .from("thread_messages")
                  .upsert([dbUserMsg], { onConflict: "id" });

                if (error) {
                  console.error(
                    "Error saving clarification user message:",
                    error
                  );
                  // Try fallback save with saveUserMessage
                  try {
                    await saveUserMessage(dbUserMsg);
                  } catch (fallbackError) {
                    console.error("Fallback save also failed:", fallbackError);
                  }
                } else {
                  console.log(
                    "🔬 Successfully saved clarification user message to database"
                  );
                }
              } catch (dbSaveError) {
                console.error(
                  "Error saving clarification user message to database:",
                  dbSaveError
                );
                // Try fallback save with saveUserMessage
                try {
                  await saveUserMessage({
                    id: userMessageId,
                    message_id: userMessageId,
                    thread_id: validThreadId,
                    workspace_id: currentWorkspaceId,
                    role: "user",
                    content: content,
                    created_at: timestamp,
                    updated_at: timestamp,
                    provider: "user",
                    metadata: {
                      isDeepResearchClarification: true,
                      deepResearchPhase: "clarifications",
                      originalQuery: originalDeepResearchQuery,
                      timestamp: timestamp,
                    },
                  });
                  console.log(
                    "🔬 Fallback save successful for clarification user message"
                  );
                } catch (fallbackError) {
                  console.error(
                    "All save attempts failed for clarification user message:",
                    fallbackError
                  );
                }
              }

              // Stream confirmation message as assistant response
              const assistantMessageId = fileUtils.generateUUID();

              // Initialize streaming state
              const streamState = initializeStreamState();
              useThreadStore.setState(streamState);

              // Create initial assistant message in thread store
              const initialAssistantMessage = {
                id: assistantMessageId,
                message_id: assistantMessageId,
                thread_id: validThreadId,
                workspace_id: currentWorkspaceId,
                role: "assistant",
                content: "",
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                isStreaming: true,
                messageStatus: "created",
                skipDatabaseSave: true,
                metadata: {
                  isDeepResearchConfirmation: true,
                  deepResearchPhase: "confirming",
                  originalQuery: originalDeepResearchQuery,
                  userClarifications: content,
                  hideActionButtons: true, // Hide action buttons for confirmation messages
                  isIntermediateMessage: true, // Mark as intermediate step
                  isDeepResearchPhase2: true, // Mark as Phase 2 message
                },
              };

              // Add assistant message to thread store
              useThreadStore.setState((state) => ({
                ...state,
                messages: [...(state.messages || []), initialAssistantMessage],
              }));

              // Get current model and provider for streaming
              const modelStore = useModelStore.getState();
              const currentModel = currentAssistant?.model || "gpt-4.1-mini";
              const providerName = modelStore.getEffectiveProvider();

              // Prepare message history with proper system prompt for query rewriting
              // Use ref value as fallback in case state was cleared
              const storedOriginalQuery =
                originalDeepResearchQuery ||
                originalDeepResearchQueryRef.current;
              console.log("🔬 [Rewriting Phase] Preparing rewriting message");
              console.log(
                "🔬 [Rewriting Phase] originalDeepResearchQuery (state):",
                originalDeepResearchQuery
              );
              console.log(
                "🔬 [Rewriting Phase] originalDeepResearchQueryRef (ref):",
                originalDeepResearchQueryRef.current
              );
              console.log(
                "🔬 [Rewriting Phase] Using stored query:",
                storedOriginalQuery
              );
              console.log(
                "🔬 [Rewriting Phase] user clarifications (content):",
                content
              );
              const rewritingMessageHistory = [
                {
                  role: "system",
                  content: REWRITING_PROMPT,
                },
                {
                  role: "user",
                  content: `Original query: ${storedOriginalQuery}\n\nUser clarifications: ${content}`,
                },
              ];
              console.log(
                "🔬 [Rewriting Phase] Rewriting message content:",
                rewritingMessageHistory[1].content
              );

              // Stream confirmation with rewritten query
              await streamProviderChat({
                providerName: providerName,
                modelName: currentModel,
                messages: rewritingMessageHistory,
                instructions: REWRITING_PROMPT, // Use the system prompt directly
                threadStore: {
                  getState: useThreadStore.getState,
                  setState: useThreadStore.setState,
                },
                updateMessageState: enhancedUpdateMessageState,
                userId: userId,
                contextData: {
                  isDeepResearchConfirmation: true,
                  deepResearchPhase: "confirming",
                  originalQuery: originalDeepResearchQuery,
                  userClarifications: content,
                  workspace_id: currentWorkspaceId,
                },
                messageId: assistantMessageId,
                threadId: validThreadId,
                workspace_id: currentWorkspaceId,
                saveToDatabase: async (msgData) => {
                  try {
                    if (
                      msgData.isStreaming === true ||
                      msgData.skipDatabaseSave === true
                    ) {
                      return { success: true, id: msgData.id };
                    }

                    await saveAssistantMessage({
                      ...msgData,
                      metadata: {
                        ...msgData.metadata,
                        isDeepResearchConfirmation: true,
                        deepResearchPhase: "confirming",
                        originalQuery: originalDeepResearchQuery,
                        userClarifications: content,
                        hideActionButtons: true, // Hide action buttons for confirmation messages
                        isIntermediateMessage: true, // Mark as intermediate step
                        isDeepResearchPhase2: true, // Mark as Phase 2 message
                      },
                    });
                    return { success: true, id: msgData.id };
                  } catch (error) {
                    console.error("Message database save error:", error);
                    return { success: false, error: error.message };
                  }
                },
              });

              // Scroll to bottom
              setTimeout(() => {
                if (scrollToBottom) {
                  scrollToBottom();
                }
              }, 100);

              // Wait a moment then generate title and start the actual deep research
              setTimeout(async () => {
                setDeepResearchPhase("researching");

                // ENHANCED: Generate title based on combined query and clarifications
                try {
                  // Create comprehensive query for title generation
                  const comprehensiveQuery = `${originalDeepResearchQuery}\n\nAdditional context and clarifications: ${content}`;

                  // Generate title immediately using the enhanced query
                  const { generateTitleImmediately } =
                    useThreadStore.getState();
                  await generateTitleImmediately(
                    validThreadId,
                    comprehensiveQuery
                  );

                  console.log(
                    "🔬 Generated title for deep research thread based on original query + clarifications"
                  );
                } catch (titleError) {
                  console.error(
                    "Error generating title after clarifications:",
                    titleError
                  );
                  // Continue with deep research even if title generation fails
                }

                // Now start the actual deep research with the enhanced query
                const deepResearchMessageId = fileUtils.generateUUID();
                const timestamp = new Date().toISOString();

                // Lock thread ID for deep research
                if (validThreadId) {
                  deepResearchThreadIdRef.current = validThreadId;
                }

                // Create an enhanced query combining original query and clarifications
                // Use ref value as fallback in case state was cleared
                const storedOriginalQuery =
                  originalDeepResearchQuery ||
                  originalDeepResearchQueryRef.current;
                console.log("🔬 [Phase 2] Creating enhanced query");
                console.log(
                  "🔬 [Phase 2] originalDeepResearchQuery (state):",
                  originalDeepResearchQuery
                );
                console.log(
                  "🔬 [Phase 2] originalDeepResearchQueryRef (ref):",
                  originalDeepResearchQueryRef.current
                );
                console.log(
                  "🔬 [Phase 2] Using stored query:",
                  storedOriginalQuery
                );
                console.log("🔬 [Phase 2] Clarification content:", content);
                console.log(
                  "🔬 [Phase 2] Clarification content length:",
                  content?.length
                );
                const enhancedQuery = `${storedOriginalQuery}\n\nAdditional context and clarifications: ${content}`;
                console.log(
                  "🔬 [Phase 2] Enhanced query created:",
                  enhancedQuery
                );
                console.log(
                  "🔬 [Phase 2] Enhanced query length:",
                  enhancedQuery?.length
                );

                // PRE-INITIALIZE: Create the assistant message with deep research metadata IMMEDIATELY
                // This is the same pattern used for reasoning models - creates message synchronously before async operations
                const initialDeepResearchMessage = {
                  id: deepResearchMessageId,
                  message_id: deepResearchMessageId,
                  thread_id: validThreadId,
                  workspace_id: currentWorkspaceId,
                  role: "assistant",
                  content: "", // Empty initially, will be filled with final analysis
                  created_at: timestamp,
                  updated_at: timestamp,
                  isStreaming: true,
                  provider: providerName || "openai",
                  metadata: {
                    provider: providerName || "openai",
                    model: currentModel,
                    isDeepResearch: true,
                    isDeepResearchInProgress: true,
                    originalQueryMessageId: userMessageId,
                    deepResearch: {
                      status: "active",
                      activity: [],
                      sources: [],
                      query: enhancedQuery,
                      startTime: Date.now(),
                      activitiesCount: 0,
                      sourcesCount: 0,
                      elapsedTime: 0,
                      progressPercentage: 0,
                    },
                  },
                };

                // Add the message to the store IMMEDIATELY (synchronously) - this is the key!
                useThreadStore.setState((state) => ({
                  ...state,
                  messages: [
                    ...(state.messages || []),
                    initialDeepResearchMessage,
                  ],
                  preventNewMessageClear: true,
                }));

                try {
                  await handleDeepResearch(
                    userMessageId, // Use the clarification message ID
                    enhancedQuery, // Use the enhanced query with clarifications
                    validThreadId,
                    deepResearchMessageId
                  );
                } catch (researchError) {
                  console.error("Error starting deep research:", researchError);
                  toast({
                    title: "Deep Research Error",
                    description: "Failed to start deep research process.",
                    variant: "destructive",
                  });
                }
              }, 2000); // 2 second delay to show confirmation message

              return; // Exit early
            } catch (error) {
              console.error("Error in clarifications processing phase:", error);
              toast({
                title: "Deep Research Error",
                description:
                  "Failed to process clarifications. Please try again.",
                variant: "destructive",
              });

              // Reset state and fall back to regular chat
              setDeepResearchPhase("disabled");
              setIsDeepResearchEnabled(false);
              return;
            }
          }
        }

        // Phase 1: Initial query - generate clarifying questions
        // This runs for initial deep research queries ONLY if we haven't asked questions yet
        // CRITICAL FIX: Also include "researching" phase if no clarifying questions have been asked yet
        if (
          (deepResearchPhase === "disabled" ||
            deepResearchPhase === "clarifying" ||
            (deepResearchPhase === "researching" &&
              !hasAskedClarifyingQuestions)) &&
          !hasAskedClarifyingQuestions
        ) {
          console.log("🔬 Entering Phase 1: Generating clarifying questions");
          try {
            // Store the original query and set phase
            console.log(
              "🔬 [Phase 1] Setting originalDeepResearchQuery to:",
              content
            );
            console.log("🔬 [Phase 1] Query length:", content?.length);
            setOriginalDeepResearchQuery(content);
            originalDeepResearchQueryRef.current = content; // Also store in ref for persistence
            setDeepResearchPhase("clarifying");

            // Step 1: Get or create thread for deep research
            let validThreadId = currentThread?.id || currentThreadId;
            const isOnNewChatRoute = location.pathname === "/chat/new";

            // CRITICAL FIX: Create thread if we don't have one (same pattern as regular messages)
            if (!validThreadId || isOnNewChatRoute) {
              // First check if there's already a pending thread
              const threadStore = useThreadStore.getState();
              const pendingThread = threadStore.getPendingNewChatThread?.();

              if (pendingThread) {
                const existingThread =
                  await threadStore.fetchThread(pendingThread);
                if (existingThread) {
                  validThreadId = pendingThread;
                  threadStore.setCurrentThread(existingThread);
                  threadStore.setCurrentThreadId(pendingThread);
                  threadStore.clearPendingNewChatThread?.();
                }
              }

              // If still no thread, create one
              if (!validThreadId) {
                // Use fast thread creation with optimistic ID
                const newThread = await createThreadOptimistic({
                  thread_id: optimisticThreadId, // Use the optimistic ID from above
                  workspace_id: currentWorkspaceId,
                  isWorkspaceChat: false,
                  needsVectorStore: true,
                  metadata: {
                    skip_automatic_title_generation: true,
                    awaiting_deep_research_title: true,
                    deep_research_enabled: true,
                    created_for_deep_research: true,
                  },
                });

                if (!newThread?.id) {
                  throw new Error("Failed to create thread for deep research");
                }

                validThreadId = newThread.id;

                // Set thread store state
                threadStore.setCurrentThread(newThread);
                threadStore.setCurrentThreadId(validThreadId);

                // Navigate to the new thread immediately
                if (location.pathname === "/chat/new") {
                  console.log(
                    `[Deep Research] Navigating to new thread ${validThreadId}`
                  );
                  navigate(`/chat/${validThreadId}`, { replace: false });
                }
              }
            }

            // Lock the thread ID to prevent race conditions
            threadIdRef.current = validThreadId;
            deepResearchThreadIdRef.current = validThreadId;

            // Set active deep research thread ID in thread store for Phase 1
            useThreadStore
              .getState()
              .setActiveDeepResearchThreadId(validThreadId);

            // Step 2: Add user message after thread creation and navigation
            const timestamp = new Date().toISOString();
            const userMessageId = fileUtils.generateUUID();

            const userMsg = {
              id: userMessageId,
              message_id: userMessageId,
              thread_id: validThreadId,
              workspace_id: currentWorkspaceId,
              role: "user",
              content: content,
              created_at: timestamp,
              updated_at: timestamp,
              metadata: {
                isDeepResearchQuery: true,
                deepResearchPhase: "initial",
                timestamp: timestamp,
              },
            };

            // Add user message directly to thread store
            useThreadStore.setState((state) => ({
              ...state,
              messages: [...(state.messages || []), userMsg],
              preventNewMessageClear: true,
            }));

            // CRITICAL FIX: Save the initial deep research user message to database
            try {
              const { supabase } = await import("@/lib/supabase");

              const dbUserMsg = {
                id: userMessageId,
                message_id: userMessageId,
                thread_id: validThreadId,
                workspace_id: currentWorkspaceId,
                role: "user",
                content: content,
                created_at: timestamp,
                updated_at: timestamp,
                provider: "user",
                metadata: {
                  isDeepResearchQuery: true,
                  deepResearchPhase: "initial",
                  timestamp: timestamp,
                },
              };

              const { error } = await supabase
                .from("thread_messages")
                .upsert([dbUserMsg], { onConflict: "id" });

              if (error) {
                console.error(
                  "Error saving initial deep research user message:",
                  error
                );
                // Try fallback save with saveUserMessage
                try {
                  await saveUserMessage(dbUserMsg);
                } catch (fallbackError) {
                  console.error("Fallback save also failed:", fallbackError);
                }
              } else {
                console.log(
                  "🔬 Successfully saved initial deep research user message to database"
                );
              }
            } catch (dbSaveError) {
              console.error(
                "Error saving initial deep research user message to database:",
                dbSaveError
              );
              // Try fallback save with saveUserMessage
              try {
                await saveUserMessage({
                  id: userMessageId,
                  message_id: userMessageId,
                  thread_id: validThreadId,
                  workspace_id: currentWorkspaceId,
                  role: "user",
                  content: content,
                  created_at: timestamp,
                  updated_at: timestamp,
                  provider: "user",
                  metadata: {
                    isDeepResearchQuery: true,
                    deepResearchPhase: "initial",
                    timestamp: timestamp,
                  },
                });
                console.log(
                  "🔬 Fallback save successful for initial deep research user message"
                );
              } catch (fallbackError) {
                console.error(
                  "All save attempts failed for initial deep research user message:",
                  fallbackError
                );
              }
            }

            // Step 5: Stream clarifying questions
            await streamClarifyingQuestions(content, validThreadId);

            // CRITICAL FIX: Ensure deep research stays enabled and phase is set correctly
            setIsDeepResearchEnabled(true);
            // Phase is already set in streamClarifyingQuestions, don't override it here
            console.log(
              "🔬 Phase 1 completed, phase should be 'awaiting_clarifications'"
            );

            // Scroll to bottom
            setTimeout(() => {
              if (scrollToBottom) {
                scrollToBottom();
              }
            }, 100);

            return; // Exit early, wait for user clarifications
          } catch (error) {
            console.error("Error in clarifying questions phase:", error);
            toast({
              title: "Deep Research Error",
              description:
                "Failed to generate clarifying questions. Starting research with original query.",
              variant: "destructive",
            });
            // Fall through to original deep research logic
          }
        }
      }

      // CRITICAL FIX: Define isWorkspaceChat using the already defined combinedContextData
      const pendingWorkspaceChat = localStorage.getItem("pendingWorkspaceChat");
      const isWorkspaceChat =
        !!pendingWorkspaceChat ||
        combinedContextData?.from_workspace_chat === true ||
        combinedContextData?.workspace_initiated === true ||
        combinedContextData?.is_workspace_chat === true ||
        metadata?.workspace_origin === true;

      // Always use currentWorkspaceId for message saving since messages require workspace_id
      // The workspace_id is needed for database constraints, but is_team flags control workspace behavior
      const workspaceIdToCheck =
        combinedContextData?.workspace_id ||
        pendingWorkspaceChat ||
        currentWorkspaceId;

      // SIMPLIFIED: Vector store handling is now managed by thread store
      // No need to manage pendingVectorStoreId in localStorage

      // RACE CONDITION FIX: Capture thread ID in ref to prevent race conditions during streaming
      // CRITICAL FIX: Lock the thread ID immediately when handleSendMessage is called
      // This ensures that even if the user navigates away, we use the thread that was active when they sent the message
      const threadIdAtSendTime = currentThreadId || currentThread?.id;
      if (threadIdAtSendTime && !threadIdRef.current) {
        threadIdRef.current = threadIdAtSendTime;
      }
      const effectiveThreadId = threadIdRef.current || threadIdAtSendTime;

      if (currentThreadId && !deepResearchEnabled) {
        const threadStore = useThreadStore.getState();
        const currentMessages = threadStore.messages || [];
        const isFirstUserMessage =
          currentMessages.filter((m) => m.role === "user").length === 0;
        const hasDefaultTitle =
          !currentThread.title ||
          currentThread.title === "New Chat" ||
          currentThread.title === "New Thread" ||
          currentThread.title === "Untitled Thread";

        if (isFirstUserMessage || hasDefaultTitle) {
          // Use thread store for title generation instead of localStorage
          // CHANGED: Skip for deep research - title will be generated in Phase 2
          const { enqueueTitleGeneration } = useThreadStore.getState();
          enqueueTitleGeneration(currentThreadId, content);
        }
      }

      preserveMessagesBeforeSend();

      let threadId = currentThread?.id;

      // RACE CONDITION FIX: Update threadId ref if we have a current thread
      // If we already have a locked thread ID from send time, use that instead
      if (threadIdRef.current) {
        threadId = threadIdRef.current;
      } else if (threadId) {
        threadIdRef.current = threadId;
      }

      try {
        useThreadStore.setState((state) => ({
          ...state,
          isStreaming: false,
          streamStatus: null,
          messageStatus: null,
          isStreamComplete: true,
          toolExecutionActive: false,
          isToolCallStreaming: false,
          isHandlingMessage: false,
          error: null,
          messages: state.messages,
          preventNewMessageClear: true,
        }));
      } catch (resetError) {
        console.error("Error stopping stream:", resetError);
      }

      if (preventAutomaticThread) {
        setPreventAutomaticThread(false);
        localStorage.removeItem("preventAutomaticThread");
      }

      try {
        let fileMetadata = combinedContextData?.fileMetadata || {};

        const timestamp = new Date().toISOString();
        let userMessageId = fileUtils.generateUUID();

        // Check if we need to replace an optimistic message
        const existingOptimisticMessage = messages.find(
          (msg) =>
            msg.metadata?.isOptimistic === true &&
            msg.metadata?.needsReplacement === true &&
            msg.role === "user"
        );

        if (existingOptimisticMessage) {
          // Use the same ID as the optimistic message to replace it
          userMessageId = existingOptimisticMessage.id;
        }

        const imageFiles =
          storageFiles?.filter(
            (file) =>
              file.isImage ||
              file.type?.startsWith("image/") ||
              file.needsVision === true
          ) || [];

        const pdfFiles =
          storageFiles?.filter(
            (file) =>
              file.isPdf ||
              file.type === "application/pdf" ||
              file.name?.toLowerCase().endsWith(".pdf")
          ) || [];

        const visionImageUrls = imageFiles
          .filter(
            (file) =>
              file.visionMetadata?.publicUrl ||
              file.data?.publicUrl ||
              file.publicUrl ||
              file.url
          )
          .map(
            (file) =>
              file.visionMetadata?.publicUrl ||
              file.data?.publicUrl ||
              file.publicUrl ||
              file.url
          )
          .filter(Boolean);

        const pdfFileIds = pdfFiles
          .filter((file) => file.openai_file_id || file.file_id)
          .map((file) => file.openai_file_id || file.file_id)
          .filter(Boolean);

        if (storageFiles && storageFiles.length > 0) {
          try {
            const fileStorageStoreModule = await import(
              "@/store/fileStorageStore"
            );
            const fileStorageStore = fileStorageStoreModule.default;

            fileStorageStore.getState().clearCurrentFiles();

            storageFiles.forEach((file) => {
              const fileData = {
                id: file.openai_file_id || file.id,
                openai_file_id: file.openai_file_id,
                name: file.name,
                type: file.type,
                size: file.size,
                path: file.path || file.storage_path,
                storage_path: file.storage_path || file.path,
                thread_id: currentThreadId,
                isImage:
                  file.isImage || file.type?.startsWith("image/") || false,
                needsVision:
                  file.isImage || file.type?.startsWith("image/") || false,
                bucketName: file.bucketName || "chat",
                publicUrl:
                  file.publicUrl ||
                  file.url ||
                  (file.data && file.data.publicUrl),
                created_at: new Date().toISOString(),
                visionMetadata:
                  file.visionMetadata ||
                  (file.isImage
                    ? {
                        publicUrl:
                          file.publicUrl ||
                          file.url ||
                          (file.data && file.data.publicUrl) ||
                          (file.visionMetadata &&
                            file.visionMetadata.publicUrl),
                        contentType: file.type,
                        isReady: true,
                      }
                    : null),
              };

              fileStorageStore.getState().addCurrentFile(fileData);
            });
          } catch (fileStoreError) {
            console.error(
              "Error adding files to fileStorageStore:",
              fileStoreError
            );
          }
        }

        const filePathsForMetadata =
          storageFiles?.map((file) => {
            if (!file) {
              return {
                file_id: null,
                name: "Unknown file",
                type: "application/octet-stream",
                isImage: false,
              };
            }

            // Handle both direct file objects and nested data structures
            const fileData = file.data || file;
            const fileType =
              file.type || fileData.type || "application/octet-stream";

            return {
              file_id: file.openai_file_id || file.file_id,
              openai_file_id: file.openai_file_id || file.file_id,
              name: file.name || fileData.name || "Unnamed file",
              type: fileType,
              isImage:
                file.isImage ||
                (fileType && fileType.startsWith("image/")) ||
                file.needsVision === true,
              isPdf:
                file.isPdf ||
                file.type === "application/pdf" ||
                file.name?.toLowerCase().endsWith(".pdf"),
              isDocument: file.isDocument || file.needsAnalysis === true,
              isGoogleDrive: file.isGoogleDrive || false,
              googleDriveId: file.googleDriveId,
              supabase_file_id:
                file.path ||
                fileData.path ||
                file.storage_path ||
                file.supabase_file_id,
              path: file.path || fileData.path || file.storage_path,
              storage_path: file.storage_path || file.path || fileData.path,
              publicUrl: file.publicUrl || fileData.publicUrl || file.url,
              url: file.url || file.publicUrl || fileData.publicUrl,
              size: file.size || fileData.size || 0,
              visionMetadata: file.visionMetadata,
              vector_store_attached: file.vector_store_attached,
              vector_store_id: file.vector_store_id,
              vector_store_file_id: file.vector_store_file_id,
            };
          }) || [];

        const filePathsWithOpenAIIds = filePathsForMetadata.map((filePath) => {
          if (filePath.openai_file_id) {
            return filePath;
          }

          const matchingStorageFile = storageFiles?.find(
            (sf) =>
              sf.path === filePath.path ||
              sf.id === filePath.id ||
              sf.name === filePath.name
          );

          if (matchingStorageFile?.openai_file_id) {
            return {
              ...filePath,
              openai_file_id: matchingStorageFile.openai_file_id,
              file_id: matchingStorageFile.openai_file_id,
              visionMetadata: matchingStorageFile.isImage
                ? {
                    publicUrl:
                      matchingStorageFile.publicUrl ||
                      matchingStorageFile.url ||
                      (matchingStorageFile.data &&
                        matchingStorageFile.data.publicUrl) ||
                      (matchingStorageFile.visionMetadata &&
                        matchingStorageFile.visionMetadata.publicUrl),
                    contentType: matchingStorageFile.type,
                    isReady: true,
                  }
                : null,
            };
          }

          const matchingFile = files?.find(
            (f) =>
              f.path === filePath.path ||
              f.id === filePath.id ||
              f.name === filePath.name
          );

          if (matchingFile?.openai_file_id) {
            return {
              ...filePath,
              openai_file_id: matchingFile.openai_file_id,
              file_id: matchingFile.openai_file_id,
              visionMetadata: matchingFile.isImage
                ? {
                    publicUrl:
                      matchingFile.publicUrl ||
                      matchingFile.url ||
                      (matchingFile.data && matchingFile.data.publicUrl),
                    contentType: matchingFile.type,
                    isReady: true,
                  }
                : null,
            };
          }

          return filePath;
        });

        try {
          let threadId = currentThread?.id;
          let isNewThread = false;
          let newThread = null;
          let threadVectorStoreId = null;

          // CRITICAL CHANGE: Check if thread was already created by useNewChat
          const { getPendingNewChatThread } = useThreadStore.getState();
          const newChatThreadId = getPendingNewChatThread();

          // CRITICAL FIX: Also check if we're on /chat/new route
          const isOnNewChatRoute = location.pathname === "/chat/new";

          if (!threadId || isOnNewChatRoute) {
            // First check if we have a thread created by useNewChat
            if (newChatThreadId) {
              try {
                // Fetch the thread to ensure it exists and set it as current
                const threadStore = useThreadStore.getState();
                const existingThread =
                  await threadStore.fetchThread(newChatThreadId);

                if (existingThread) {
                  threadId = newChatThreadId;
                  newThread = existingThread;
                  isNewThread = true; // Treat as new for first message handling

                  // Set as current thread
                  threadStore.setCurrentThread(existingThread);
                  threadStore.setCurrentThreadId(newChatThreadId);

                  // Clean up thread store flags
                  const { clearPendingNewChatThread } =
                    useThreadStore.getState();
                  clearPendingNewChatThread();

                  // Navigate to the specific thread URL now that we're using it
                  const currentPath = window.location.pathname;
                  if (currentPath === "/chat/new") {
                    setTimeout(() => {
                      navigate(`/chat/${threadId}`);
                    }, 100);
                  }
                } else {
                  console.warn(
                    `⚠️ Thread ${newChatThreadId} not found, creating new one`
                  );
                  // Fall through to create new thread
                }
              } catch (fetchError) {
                console.error(
                  "❌ Error fetching useNewChat thread:",
                  fetchError
                );
                // Fall through to create new thread
              }
            }

            // If we still don't have a thread, create one
            if (!threadId) {
              try {
                // Fast thread creation with optimistic approach
                // Vector store will be created by thread store when needed

                // Clean up localStorage flags
                if (pendingWorkspaceChat) {
                  localStorage.removeItem("pendingWorkspaceChat");
                  localStorage.removeItem("pendingWorkspaceType");
                }

                // CRITICAL: Check if we should preserve user messages
                const shouldPreserveUserMessages =
                  localStorage.getItem("preserveUserMessages") === "true";
                if (shouldPreserveUserMessages) {
                  localStorage.removeItem("preserveUserMessages");
                }

                // Determine workspace team status for thread flags
                let isTeamWorkspace = false;
                if (isWorkspaceChat && workspaceIdToCheck) {
                  try {
                    const { workspaces, currentWorkspace } =
                      useAuthStore.getState();
                    let workspace =
                      workspaces?.find((w) => w.id === workspaceIdToCheck) ||
                      (currentWorkspace?.id === workspaceIdToCheck
                        ? currentWorkspace
                        : null);

                    if (workspace?.is_team === true) {
                      isTeamWorkspace = true;
                    }
                  } catch (error) {
                    console.warn(
                      "Error checking workspace team status:",
                      error
                    );
                  }
                }

                // Use fast thread creation
                // OPTIMISTIC RENDERING FIX: Pass optimistic thread ID to use
                newThread = await createThreadOptimistic({
                  thread_id: optimisticThreadId, // Use the optimistic ID
                  workspace_id: workspaceIdToCheck,
                  isWorkspaceChat,
                  needsVectorStore: true,
                  metadata: {
                    is_team: isTeamWorkspace,
                    is_shared_thread: isTeamWorkspace,
                    shared_in_workspace: isTeamWorkspace
                      ? workspaceIdToCheck
                      : null,
                    from_workspace_chat: isWorkspaceChat,
                    should_add_to_workspace_threads: isWorkspaceChat,
                    // ADDED: Prevent automatic title generation for deep research threads
                    ...(deepResearchEnabled && {
                      skip_automatic_title_generation: true,
                      awaiting_deep_research_title: true,
                    }),
                  },
                });

                threadId = newThread.id;
                isNewThread = true;

                // RACE CONDITION FIX: Lock the newly created thread ID immediately
                threadIdRef.current = threadId;
              } catch (createError) {
                console.error("Fast thread creation error:", createError);
                throw createError;
              }
            }

            // Set title generation for any new thread using thread store
            if (threadId && isNewThread && !deepResearchEnabled) {
              // Use thread store for title generation instead of localStorage
              // CHANGED: Skip automatic title generation for deep research - it will be handled in Phase 2
              const { enqueueTitleGeneration } = useThreadStore.getState();
              enqueueTitleGeneration(threadId, content);
            }
          } else {
            // SIMPLIFIED: Vector store will be ensured by thread store when files are uploaded
            // or when retrieval is needed during streaming
          }
          if (newThread && newThread.id) {
            threadId = newThread.id;
            isNewThread = true;
          }

          const streamState = initializeStreamState();

          // Apply streaming state while preserving existing messages
          useThreadStore.setState((state) => {
            const existingMessages = state.messages || [];

            return {
              // Apply streaming state
              ...streamState,
              messages: existingMessages,
              preventNewMessageClear: true,
            };
          });

          const messageContext = {
            ...(contextData || {}),
            instructions: currentAssistant?.instructions,
            timestamp: new Date().toISOString(),
            has_vision_enabled: imageFiles.length > 0,
            vision_image_urls: visionImageUrls,
            has_images: imageFiles.length > 0,
            image_count: imageFiles.length,
            has_pdfs: pdfFiles.length > 0,
            pdf_count: pdfFiles.length,
            pdf_file_ids: pdfFileIds,
          };

          // Check for optimistic message before creating user message
          let optimisticMessageToReplace = null;
          if (isWorkspaceChat || contextData?.from_workspace_chat) {
            const currentMessages = useThreadStore.getState().messages || [];
            optimisticMessageToReplace = currentMessages.find(
              (msg) =>
                msg.metadata?.isOptimistic === true &&
                msg.metadata?.needsReplacement === true &&
                msg.role === "user"
            );

            if (optimisticMessageToReplace) {
              // Use the optimistic message's ID
              userMessageId = optimisticMessageToReplace.id;
            }
          }

          let userMsg;
          try {
            // VALIDATION GATE: Verify thread hasn't changed before creating user message
            if (threadIdRef.current && threadIdRef.current !== threadId) {
              console.warn(
                "[ChatContext] Thread navigation detected during message send - aborting",
                { expectedThread: threadIdRef.current, currentThread: threadId }
              );
              throw new Error(
                "Thread navigation detected - message send aborted"
              );
            }

            userMsg = await createUserMessageWithValidatedFiles({
              userMessageId,
              threadId,
              content,
              timestamp,
              // CRITICAL FIX: Only pass workspace ID if this is a workspace chat
              currentWorkspaceId: isWorkspaceChat ? workspaceIdToCheck : null,
              storageFiles,
              filePathsWithOpenAIIds,
              messageContext,
              visionImageUrls,
              imageFiles,
              pdfFiles,
              pdfFileIds,
            });

            // VALIDATION GATE: Verify thread lock still matches after async operation
            if (threadIdRef.current && threadIdRef.current !== threadId) {
              console.warn(
                "[ChatContext] Thread changed during user message creation - discarding message",
                { lockedThread: threadIdRef.current, messageThread: threadId }
              );
              throw new Error("Thread changed during message creation");
            }

            const validationResult = validateUserMessageIntegrity(userMsg);
            if (!validationResult.isValid) {
              repairUserMessage(
                userMsg,
                validationResult.issues,
                storageFiles,
                filePathsWithOpenAIIds
              );
            }

            // OPTIMISTIC RENDERING: Add provider metadata to real user message
            // This ensures the correct provider icon shows immediately
            if (!userMsg.metadata) {
              userMsg.metadata = {};
            }
            userMsg.metadata.provider = providerName;
            userMsg.metadata.model = currentModel;

            // Store canvas mode enabled state in user message metadata
            // This is used to determine if canvas should auto-open
            // Default to false, only set to true when canvas mode is explicitly enabled
            const effectiveCanvasModeEnabled =
              capturedCanvasModeEnabled || isCanvasModeEnabled;
            userMsg.metadata.canvasModeEnabled =
              effectiveCanvasModeEnabled === true;

            // OPTIMISTIC RENDERING: Remove the optimistic message now that real message exists
            if (optimisticUserMessageId && optimisticThreadId) {
              const threadStore = useThreadStore.getState();
              threadStore.removeOptimisticMessage(
                optimisticThreadId,
                optimisticUserMessageId
              );
              console.log(
                `[Optimistic Rendering] Removed optimistic message ${optimisticUserMessageId} from thread ${optimisticThreadId}`
              );
            }
          } catch (userMsgError) {
            console.error(
              "[Optimistic Rendering] Error creating user message:",
              userMsgError
            );

            // OPTIMISTIC RENDERING: Mark optimistic message as failed
            if (optimisticUserMessageId && optimisticThreadId) {
              const threadStore = useThreadStore.getState();
              threadStore.markOptimisticMessageFailed(
                optimisticThreadId,
                optimisticUserMessageId,
                userMsgError
              );
              console.log(
                `[Optimistic Rendering] Marked optimistic message ${optimisticUserMessageId} as failed`
              );
            }

            // Re-throw the error to be handled by outer error handlers
            throw userMsgError;
          }

          // Determine if this message has files for retrieval purposes
          const messageHasFiles =
            (storageFiles && storageFiles.length > 0) ||
            (filePathsWithOpenAIIds && filePathsWithOpenAIIds.length > 0) ||
            (imageFiles && imageFiles.length > 0) ||
            (pdfFiles && pdfFiles.length > 0) ||
            userMsg.metadata?.has_files === true ||
            userMsg.metadata?.file_count > 0;

          if (storageFiles && storageFiles.length > 0) {
            if (
              !userMsg.metadata.storage_files &&
              userMsg.attachments?.length > 0
            ) {
              console.warn(
                "⚠️ storage_files metadata missing despite having attachments - reconstructing"
              );
              try {
                const fallbackStorageFiles = JSON.stringify(
                  userMsg.attachments.map((f) => ({
                    path: f.path || f.storage_path,
                    file_id: f.openai_file_id || f.file_id || f.id,
                    type: f.type,
                    name:
                      f.name || (f.path && f.path.split("/").pop()) || "file",
                    isImage: f.isImage || f.type?.startsWith("image/") || false,
                    isPdf:
                      f.isPdf ||
                      f.type === "application/pdf" ||
                      f.name?.toLowerCase().endsWith(".pdf"),
                    publicUrl:
                      f.publicUrl || f.url || (f.data && f.data.publicUrl),
                    url: f.url || f.publicUrl || f.data?.publicUrl,
                  }))
                );
                userMsg.metadata.storage_files = fallbackStorageFiles;
                userMsg.metadata.file_count = userMsg.attachments.length;
                userMsg.metadata.has_files = true;
              } catch (storageError) {
                console.error(
                  "Error reconstructing storage_files:",
                  storageError
                );
              }
            }
          }

          const isThreadSwitch = threadId !== currentThreadId;

          // CRITICAL FIX: Add user message BEFORE any thread switching or clearing
          const currentMessages = useThreadStore.getState().messages || [];
          const hasDuplicateMessage = currentMessages.some(
            (msg) =>
              msg.role === "user" &&
              msg.content === content &&
              msg.thread_id === threadId
          );

          // CRITICAL FIX: Additional check for edit operations and reruns to prevent duplicates
          const isEditOperation =
            combinedContextData?.isResponseToEdit === true ||
            combinedContextData?.isEditedMessage === true ||
            combinedContextData?.editedMessageId;

          const isRerunOperation =
            combinedContextData?.isResponseToRerun === true ||
            combinedContextData?.isRerunMessage === true ||
            combinedContextData?.rerunMessageId;

          if (isEditOperation) {
            console.warn(
              "Detected edit operation in handleSendMessage - this should not create a user message"
            );
            return;
          }

          if (isRerunOperation) {
            console.warn(
              "Detected rerun operation in handleSendMessage - this should not create a user message"
            );
            return;
          }

          // The optimistic message replacement is now handled above when creating the user message
          const shouldReplaceOptimistic = !!optimisticMessageToReplace;
          const optimisticMessageId = optimisticMessageToReplace?.id || null;

          if (shouldReplaceOptimistic) {
            // Remove the optimistic flags from metadata
            userMsg.metadata = {
              ...userMsg.metadata,
              isOptimistic: false,
              needsReplacement: false,
            };
          }

          // Add user message directly to thread store
          if (!hasDuplicateMessage) {
            // VALIDATION GATE: Final check before adding user message to ensure thread lock is valid
            const currentStoreThreadId =
              useThreadStore.getState().currentThreadId;
            if (threadIdRef.current && threadIdRef.current !== threadId) {
              console.warn(
                "[ChatContext] Thread lock mismatch when adding user message - skipping",
                {
                  lockedThread: threadIdRef.current,
                  messageThread: threadId,
                  currentStoreThread: currentStoreThreadId,
                }
              );
              throw new Error("Thread lock mismatch - user message not added");
            }

            // Also verify the store's currentThreadId matches the message's thread
            if (
              currentStoreThreadId !== threadId &&
              currentStoreThreadId !== null
            ) {
              console.warn(
                "[ChatContext] Store thread mismatch when adding user message - skipping",
                { messageThread: threadId, storeThread: currentStoreThreadId }
              );
              throw new Error("Store thread mismatch - user message not added");
            }

            useThreadStore.setState((state) => {
              let updatedMessages = state.messages || [];

              if (shouldReplaceOptimistic && optimisticMessageId) {
                // Replace the optimistic message with the real one
                updatedMessages = updatedMessages.map((msg) =>
                  msg.id === optimisticMessageId ? userMsg : msg
                );
              } else {
                // Add as new message
                updatedMessages = [...updatedMessages, userMsg];
              }

              return {
                ...state,
                messages: updatedMessages,
                preventNewMessageClear: true,
                hasJustAddedUserMessage: true,
                lastAddedUserMessageId: userMsg.id,
                lastAddedUserMessageTime: Date.now(),
              };
            });

            // Verify the message was actually added
            const verifyState = useThreadStore.getState();
            const messageInStore = verifyState.messages?.find(
              (msg) => msg.id === userMsg.id
            );
            if (!messageInStore) {
              console.warn(
                `⚠️ User message ${userMsg.id} not found in store after adding - forcing add`
              );
              useThreadStore.setState((state) => ({
                ...state,
                messages: [...(state.messages || []), userMsg],
                preventNewMessageClear: true,
                hasJustAddedUserMessage: true,
                lastAddedUserMessageId: userMsg.id,
                lastAddedUserMessageTime: Date.now(),
              }));
            }

            // CRITICAL: Store user message in backup refs to prevent loss during streaming
            messagesRef.current = useThreadStore.getState().messages || [];
            backedUpMessagesRef.current = [...messagesRef.current];

            // Set a flag to protect this user message throughout the streaming process
            localStorage.setItem(
              `protectUserMessage_${userMsg.id}`,
              JSON.stringify({
                id: userMsg.id,
                content: userMsg.content,
                timestamp: Date.now(),
                threadId: threadId,
              })
            );
          }

          // CRITICAL FIX: Ensure messages are sorted chronologically and deduplicated
          // Get the current messages and sort them properly
          const currentStoreState = useThreadStore.getState();
          let orderedMessages = currentStoreState.messages || [];

          // Find the user message in the current messages
          const userMessageIndex = orderedMessages.findIndex(
            (msg) => msg.id === userMsg.id
          );

          if (userMessageIndex !== -1) {
            // Sort messages chronologically (simple sort without complex deduplication)
            orderedMessages.sort((a, b) => {
              const dateA = new Date(a.created_at || a.timestamp || Date.now());
              const dateB = new Date(b.created_at || b.timestamp || Date.now());
              return dateA - dateB;
            });

            // Update the thread store with properly ordered messages
            useThreadStore.setState((state) => ({
              ...state,
              messages: orderedMessages,
              preventNewMessageClear: true,
              hasJustAddedUserMessage: true,
              lastAddedUserMessageId: userMsg.id,
              lastAddedUserMessageTime: Date.now(),
            }));
          }

          // Now handle thread switching AFTER user message is safely added and positioned
          if (isThreadSwitch || isNewThread) {
            // Get updated messages after user message was added and positioned
            const updatedMessages = useThreadStore.getState().messages || [];
            const userMessageInStore = updatedMessages.find(
              (msg) => msg.id === userMsg.id
            );

            if (userMessageInStore) {
              // Preserve the user message during thread switch
              useThreadStore.setState({
                messages: [userMessageInStore], // Keep only the user message
                preventNewMessageClear: true,
                hasJustAddedUserMessage: true,
                lastAddedUserMessageId: userMsg.id,
              });
              messagesRef.current = [userMessageInStore];
            } else {
              console.warn(
                "User message not found in store during thread switch"
              );
              useThreadStore.setState({
                messages: [userMsg], // Ensure user message is preserved
                preventNewMessageClear: true,
                hasJustAddedUserMessage: true,
                lastAddedUserMessageId: userMsg.id,
              });
              messagesRef.current = [userMsg];
            }
          }

          // Database save with improved error handling
          if (!hasDuplicateMessage) {
            try {
              const { supabase } = await import("@/lib/supabase");

              const dbUserMsg = {
                id: userMessageId,
                message_id: userMessageId,
                thread_id: threadId,
                workspace_id: workspaceIdToCheck,
                role: "user", // CRITICAL: Explicitly set role to "user"
                content: content || " ",
                created_at: timestamp,
                updated_at: timestamp,
                provider: "user",
                metadata: userMsg.metadata,
              };

              if (storageFiles?.length > 0) {
                const hasFileMetadata =
                  dbUserMsg.metadata?.storage_files ||
                  dbUserMsg.metadata?.file_paths_metadata ||
                  dbUserMsg.metadata?.has_files === true;

                if (!hasFileMetadata) {
                  console.warn(
                    "⚠️ File metadata missing before database save - adding it now"
                  );
                  try {
                    dbUserMsg.metadata = {
                      ...dbUserMsg.metadata,
                      has_files: true,
                      file_count: storageFiles.length,
                      storage_files: JSON.stringify(
                        storageFiles.map((f) => {
                          const fileData = f.data || f;
                          return {
                            path: f.path || f.storage_path || fileData.path,
                            file_id: f.openai_file_id || f.file_id || f.id,
                            type: f.type || fileData.type,
                            name:
                              f.name ||
                              fileData.name ||
                              (f.path && f.path.split("/").pop()) ||
                              "file",
                            isImage:
                              f.isImage ||
                              f.type?.startsWith("image/") ||
                              false,
                            isPdf:
                              f.isPdf ||
                              f.type === "application/pdf" ||
                              f.name?.toLowerCase().endsWith(".pdf"),
                            isDocument:
                              f.isDocument || f.needsAnalysis || false,
                            isGoogleDrive: f.isGoogleDrive || false,
                            googleDriveId: f.googleDriveId,
                            publicUrl:
                              f.publicUrl || f.url || fileData.publicUrl,
                            url: f.url || f.publicUrl || fileData.publicUrl,
                            size: f.size || fileData.size || 0,
                            vector_store_attached:
                              f.vector_store_attached || false,
                            vector_store_id: f.vector_store_id,
                            vector_store_file_id: f.vector_store_file_id,
                          };
                        })
                      ),
                    };
                  } catch (metadataError) {
                    console.error("Error adding file metadata:", metadataError);
                  }
                }
              }

              try {
                await saveUserMessage(dbUserMsg); // Save user message directly
              } catch (queueError) {
                console.warn(
                  "Queue save failed, falling back to direct save:",
                  queueError
                );
                const { error } = await supabase
                  .from("thread_messages")
                  .upsert([dbUserMsg], { onConflict: "id" });

                if (error) {
                  console.error(
                    "Error saving user message to database:",
                    error
                  );

                  const minimalMsg = {
                    id: userMessageId,
                    message_id: userMessageId,
                    thread_id: threadId,
                    workspace_id: workspaceIdToCheck,
                    role: "user",
                    content: content || " ",
                    created_at: timestamp,
                    updated_at: timestamp,
                    metadata: {
                      ...dbUserMsg.metadata,
                      is_minimal_save: true,
                    },
                  };

                  const { error: retryError } = await supabase
                    .from("thread_messages")
                    .upsert([minimalMsg], { onConflict: "id" });
                }
              }
            } catch (dbError) {
              console.error("Failed to save user message directly:", dbError);
            }

            // setTimeout(async () => {
            //   try {
            //     const { supabase } = await import("@/lib/supabase");

            //     const { data: existingMsg } = await supabase
            //       .from("thread_messages")
            //       .select("id")
            //       .eq("id", userMessageId)
            //       .single();

            //     if (existingMsg) {
            //       return;
            //     }

            //     const guaranteedMsg = {
            //       id: userMessageId,
            //       message_id: userMessageId,
            //       thread_id: threadId,
            //       workspace_id: currentWorkspaceId,
            //       role: "user",
            //       content: content || " ",
            //       created_at: new Date().toISOString(),
            //       updated_at: new Date().toISOString(),
            //       provider: "user",
            //     };

            //     const { error } = await supabase
            //       .from("thread_messages")
            //       .insert([guaranteedMsg]);
            //   } catch (verifyError) {
            //     console.error("Verification save attempt failed:", verifyError);
            //   }
            // }, 1000);

            await enhancedUpdateMessageState(userMsg);
          }

          const ensureStreamingStarts = async () => {
            await new Promise((resolve) => setTimeout(resolve, 500));

            const isAlreadyStreaming = useThreadStore.getState().isStreaming;

            if (!isAlreadyStreaming) {
              try {
                const basicMessages = [
                  {
                    role: "user",
                    content,
                  },
                ];

                const fallbackMessageId = fileUtils.generateUUID();

                enhancedUpdateMessageState({
                  id: fallbackMessageId,
                  role: "assistant",
                  content: "",
                  created_at: new Date().toISOString(),
                  isStreaming: true,
                  messageStatus: "created",
                  skipDatabaseSave: true,
                });

                // Get the most up-to-date assistant information for emergency streaming
                const latestAssistantForEmergency =
                  useAssistantStore.getState().currentAssistant ||
                  currentAssistant;
                const emergencyModel =
                  latestAssistantForEmergency?.model ||
                  currentAssistant?.model ||
                  "gpt-4";
                const emergencyInstructions =
                  latestAssistantForEmergency?.instructions ||
                  currentAssistant?.instructions ||
                  "You are a helpful assistant";

                // CRITICAL FIX: Get provider from modelStore using effective provider method
                const modelStore = useModelStore.getState();
                let emergencyProviderName = modelStore.getEffectiveProvider();

                await streamProviderChat({
                  providerName: emergencyProviderName,
                  modelName: emergencyModel,
                  messages: basicMessages,
                  instructions: emergencyInstructions,
                  threadStore: {
                    getState: useThreadStore.getState,
                    setState: useThreadStore.setState,
                  }, // Pass store methods that work outside React components
                  updateMessageState: enhancedUpdateMessageState,
                  messageId: fallbackMessageId,
                  threadId: threadIdRef.current,
                  workspace_id: workspaceIdToCheck,
                  userId: userId,
                  // RACE CONDITION FIX: Pass threadIdRef to prevent race conditions
                  threadIdRef: threadIdRef,
                  contextData: {
                    storage_files: storageFiles
                      ? JSON.stringify(filePathsWithOpenAIIds)
                      : null,
                    has_vision_enabled: imageFiles.length > 0,
                    has_images: imageFiles.length > 0,
                    vision_image_urls: visionImageUrls,
                    has_pdfs: pdfFiles.length > 0,
                    pdf_file_ids: pdfFileIds,
                  },
                  tools:
                    latestAssistantForEmergency?.tools ||
                    currentAssistant?.tools,
                });
              } catch (emergencyError) {
                console.error("Emergency streaming failed:", emergencyError);
              }
            }
          };

          ensureStreamingStarts().catch((err) =>
            console.error("Failsafe error:", err)
          );

          // Handle navigation for new threads or existing threads
          if (isNewThread && newThread?.id) {
            if (userMsg) {
              messagesRef.current = [userMsg];

              try {
                useThreadStore.setState((state) => ({
                  ...state,
                  messages: state.messages?.length
                    ? [...state.messages, userMsg]
                    : [userMsg],
                  pendingUserMessage: userMsg,
                  pendingUserMessageId: userMsg.id,
                  preventNewMessageClear: true,
                  currentUserMessageId: userMsg.id,
                  currentThreadId: newThread.id,
                  currentThread: newThread,
                }));

                const storeState = useThreadStore.getState();
                const storeMessages = storeState.messages || [];

                if (!storeMessages.some((m) => m.id === userMsg.id)) {
                  useThreadStore.setState({
                    messages: [userMsg],
                    preventNewMessageClear: true,
                  });
                }

                // localStorage.setItem(
                //   `first_message_${newThread.id}`,
                //   JSON.stringify({
                //     message: userMsg,
                //     timestamp: Date.now(),
                //   }),
                // );
              } catch (msgError) {
                console.error(
                  "Error saving first message before navigation:",
                  msgError
                );
              }
            }

            localStorage.setItem(
              "pendingMessageForThread",
              JSON.stringify({
                thread_id: newThread.id,
                messageId: userMessageId,
                content: content,
                timestamp: timestamp,
              })
            );

            // CRITICAL FIX: Navigate when user sends message from /chat/new (desired)
            // But don't navigate when background stream completes (undesired)
            // This code is in handleSendMessage, so it only runs when user actively sends a message
            const currentPath = window.location.pathname;
            const expectedPath = `/chat/${newThread.id}`;

            // OPTIMISTIC RENDERING FIX: Don't navigate if we already navigated optimistically
            // The optimistic navigation happens earlier (line 2762), so if newThread.id matches
            // optimisticThreadId, we're already on the correct URL
            const alreadyNavigated =
              isNewThread &&
              newThread.id === optimisticThreadId &&
              currentPath === expectedPath;

            if (alreadyNavigated) {
              // Already navigated optimistically - skip second navigation
              console.log(
                `[Optimistic Rendering] Skipping duplicate navigation - already on ${expectedPath}`
              );
            } else if (currentPath === "/chat/new") {
              // User sent message from /chat/new - navigate to new thread (DESIRED BEHAVIOR)
              setTimeout(() => {
                navigate(`/chat/${newThread.id}`);
              }, 100);
            } else if (currentPath !== expectedPath) {
              // User is on a different thread - navigate to new thread
              setTimeout(() => {
                navigate(`/chat/${newThread.id}`);
              }, 100);
            }
            // If already on expected path, don't navigate
          } else if (currentThread?.id) {
            // Only navigate if we're not already on the correct thread URL
            // CRITICAL FIX: Don't navigate if user is on /chat/new (background stream completion)
            const currentPath = window.location.pathname;
            const expectedPath = `/chat/${currentThread.id}`;

            if (currentPath !== expectedPath && currentPath !== "/chat/new") {
              navigate(`/chat/${currentThread.id}`, { replace: true });
            }
          } else {
            // CRITICAL FIX: Don't navigate if user is on /chat/new (background stream completion)
            const currentPath = window.location.pathname;
            if (currentPath !== "/chat/new") {
              const lastKnownThreadId = localStorage.getItem(
                "lastVisitedThreadId"
              );
              if (lastKnownThreadId) {
                navigate(`/chat/${lastKnownThreadId}`, { replace: true });
              } else {
                navigate("/chat", { replace: true });
              }
            }
          }

          // CRITICAL FIX: Get message history from thread store and ensure user message is included
          let messageHistory = useThreadStore.getState().messages || [];

          // Ensure the user message is in the message history
          const userMessageInHistory = messageHistory.find(
            (msg) => msg.id === userMsg.id
          );
          if (!userMessageInHistory) {
            // Add user message to history if not present
            messageHistory = [...messageHistory, userMsg];
          }

          // Sort messages by created_at to ensure proper chronological order
          messageHistory.sort((a, b) => {
            const dateA = new Date(a.created_at || a.timestamp || Date.now());
            const dateB = new Date(b.created_at || b.timestamp || Date.now());
            return dateA - dateB;
          });

          if (messageHistory.length > 20) {
            const systemMessages = messageHistory
              .filter((msg) => msg.role === "system")
              .slice(0, 1);
            const latestMessages = messageHistory
              .filter((msg) => msg.role !== "system")
              .slice(-19);
            messageHistory = [...systemMessages, ...latestMessages];

            // Ensure user message is still included after truncation
            const userStillInHistory = messageHistory.find(
              (msg) => msg.id === userMsg.id
            );
            if (!userStillInHistory) {
              // Remove oldest non-system message and add user message
              const nonSystemMessages = messageHistory.filter(
                (msg) => msg.role !== "system"
              );
              if (nonSystemMessages.length > 0) {
                messageHistory = messageHistory.filter(
                  (msg) => msg.id !== nonSystemMessages[0].id
                );
                messageHistory.push(userMsg);
              }
            }
          }

          // Update thread store with the properly ordered message history
          useThreadStore.setState((state) => ({
            ...state,
            messages: messageHistory,
            preventNewMessageClear: true,
          }));

          try {
            const modelId = currentAssistant?.model || "gpt-4.1-mini";

            // CRITICAL FIX: Get provider from modelStore using effective provider method
            const modelStore = useModelStore.getState();
            let providerName = modelStore.getEffectiveProvider();

            tokenCounterRef.current = new TokenCounter(modelId, providerName);

            if (messageHistory && Array.isArray(messageHistory)) {
              tokenCounterRef.current.countPromptTokens(messageHistory);
            }

            const assistantMessageId = fileUtils.generateUUID();

            enhancedUpdateMessageState({
              id: assistantMessageId,
              role: "assistant",
              content: "",
              created_at: new Date().toISOString(),
              isStreaming: true,
              messageStatus: "created",
              skipDatabaseSave: true,
              metadata: {
                provider: providerName,
                model: modelId,
              },
            });

            // Tools are now always included by configureToolParams in streamProviderChat
            // We only need to handle tool_choice based on context flags

            // Check if this is a forced image edit request
            // image_edit tool is always included by configureToolParams
            let toolChoice = "auto";
            if (combinedContextData?.forceImageEditTool === true) {
              // Force the use of image_edit tool
              toolChoice = {
                type: "function",
                function: { name: "image_edit" },
              };
            } else if (combinedContextData?.forceWebSearchTool === true) {
              // CRITICAL FIX: Force web search tool when manually selected
              toolChoice = {
                type: "function",
                function: { name: "web_search" },
              };
            } else if (combinedContextData?.forceImageGenTool === true) {
              // Force image generation tool when manually selected
              toolChoice = {
                type: "function",
                function: { name: "image_gen" },
              };
            }

            // Check if web_search or image_gen tools should be forced based on context
            // These tools are always included in the API call by configureToolParams
            // The force flags passed in contextData control whether tool_choice is set to force their use
            // This is now handled by the UI passing forceWebSearchTool or forceImageGenTool in contextData

            let instructions =
              currentAssistant?.instructions || "You are a helpful assistant.";

            // Simplified workspace vector store handling
            if (
              isWorkspaceChat &&
              currentWorkspace?.vector_store_id &&
              !combinedContextData.vector_store_id
            ) {
              combinedContextData.vector_store_id =
                currentWorkspace.vector_store_id;
              combinedContextData.workspace_vector_store_id =
                currentWorkspace.vector_store_id;
            }

            const MAX_RETRIES = 2;
            let retryCount = 0;
            let streamSuccess = false;

            while (retryCount <= MAX_RETRIES && !streamSuccess) {
              try {
                if (retryCount > 0) {
                  await new Promise((resolve) => setTimeout(resolve, 500));
                  useThreadStore.setState({
                    isStreaming: true,
                    streamStatus: "started",
                    error: null,
                  });
                }

                // CRITICAL FIX: Ensure user message is properly positioned before streaming
                if (
                  userMsg &&
                  !messageHistory.some((m) => m.id === userMsg.id)
                ) {
                  messageHistory = [...messageHistory, userMsg];
                }

                // Ensure thread store has the user message in correct position
                useThreadStore.setState((state) => {
                  const currentMessages = state.messages || [];
                  const userMsgExists = currentMessages.some(
                    (m) => m.id === userMsg.id
                  );

                  if (!userMsgExists) {
                    // Add user message if not present
                    return {
                      ...state,
                      messages: [...currentMessages, userMsg],
                      preventNewMessageClear: true,
                    };
                  } else {
                    // Ensure user message is at the end (most recent)
                    const messagesWithoutUser = currentMessages.filter(
                      (m) => m.id !== userMsg.id
                    );
                    return {
                      ...state,
                      messages: [...messagesWithoutUser, userMsg],
                      preventNewMessageClear: true,
                    };
                  }
                });

                // Update messagesRef to maintain consistency
                messagesRef.current = useThreadStore.getState().messages || [];

                const safeVectorStoreId = (value) => {
                  if (typeof value === "string") return value;
                  if (typeof value === "object" && value !== null) {
                    return value.id || value.vector_store_id || String(value);
                  }
                  return value ? String(value) : null;
                };

                // Get the most up-to-date assistant information, especially important for model switch scenarios
                const latestAssistant =
                  useAssistantStore.getState().currentAssistant ||
                  currentAssistant;
                const effectiveModel =
                  latestAssistant?.model || currentAssistant?.model || "gpt-4";
                const effectiveInstructions =
                  latestAssistant?.instructions ||
                  currentAssistant?.instructions ||
                  "You are a helpful assistant.";
                const effectiveTools =
                  latestAssistant?.tools || currentAssistant?.tools || [];

                // Tools are now always included by configureToolParams in streamProviderChat
                // We pass an empty array since configureToolParams will add all necessary tools
                let finalTools = [];
                let canvasInstructions = "";

                // CRITICAL FIX: Use captured canvas mode state instead of reactive state
                // The reactive state might have been reset by new-chat-started event
                const effectiveCanvasModeEnabled =
                  capturedCanvasModeEnabled || isCanvasModeEnabled;

                if (effectiveCanvasModeEnabled) {
                  // Add canvas-specific instructions when canvas mode is enabled
                  canvasInstructions =
                    "\n\nCANVAS MODE ACTIVE - CRITICAL INSTRUCTIONS:\n" +
                    "1. Use ONLY the canvas_create tool - do NOT generate text content in your response\n" +
                    "2. Put ALL content in the canvas tool's 'query' parameter\n" +
                    "3. Do NOT write a text response before or after calling the canvas tool\n" +
                    "4. The canvas tool will handle all content generation and display\n" +
                    "5. Your response should ONLY be the canvas_create tool call\n" +
                    "6. Use canvas_create for initial content, canvas_edit for modifications\n" +
                    "7. The canvas will open automatically when you call the tool";

                  // CRITICAL FIX: Force canvas tool choice when canvas mode is enabled
                  // This ensures the first request in canvas mode uses canvas tools
                  combinedContextData.forceCanvasCreate = true;
                  combinedContextData.isCanvasModeEnabled = true;
                  combinedContextData.canvasModePreference = true;
                }

                // Tool selection is now controlled at the API level via tool_choice and tool inclusion
                // No need for soft instruction-based restrictions

                // BACKGROUND STREAMING: Check concurrency limit before starting stream
                const threadStore = useThreadStore.getState();
                const canStartStream = threadStore.canStartNewStream();

                if (!canStartStream) {
                  // At concurrency limit - cancel oldest non-active stream
                  const activeStreams = threadStore.getActiveStreams();
                  const currentThreadId = threadId || threadIdRef.current;

                  // Find oldest non-active stream to cancel
                  const nonActiveStreams = activeStreams.filter(
                    (stream) => stream.threadId !== currentThreadId
                  );

                  if (nonActiveStreams.length > 0) {
                    // Sort by startTime and cancel oldest
                    nonActiveStreams.sort((a, b) => a.startTime - b.startTime);
                    const oldestStream = nonActiveStreams[0];
                    threadStore.cancelStream(
                      oldestStream.threadId,
                      oldestStream.messageId
                    );

                    // Show notification
                    if (toast) {
                      toast({
                        title: "Stream Cancelled",
                        description: `Cancelled stream in another thread to start this one.`,
                        variant: "default",
                      });
                    }
                  } else {
                    // All streams are active - show error
                    if (toast) {
                      toast({
                        title: "Stream Limit Reached",
                        description: `Maximum ${threadStore.maxConcurrentStreams} concurrent streams. Please wait for one to complete.`,
                        variant: "destructive",
                      });
                    }
                    return; // Don't start stream
                  }
                }

                await streamProviderChat({
                  providerName: providerName,
                  modelName: effectiveModel,
                  messages: messageHistory,
                  instructions: effectiveInstructions + canvasInstructions,
                  tools: finalTools,
                  threadStore: {
                    getState: useThreadStore.getState,
                    setState: useThreadStore.setState,
                  }, // FIXED: Pass store methods that work outside React components
                  updateMessageState: enhancedUpdateMessageState,
                  userId: userId,
                  // RACE CONDITION FIX: Pass threadIdRef to prevent race conditions
                  threadIdRef: threadIdRef,
                  // BACKGROUND STREAMING: Pass threadId and messageId for stream registration
                  threadIdForStream: threadId || threadIdRef.current,
                  messageIdForStream: userMessageId,
                  contextData: {
                    ...(combinedContextData || {}),
                    vector_store_id: safeVectorStoreId(
                      contextData?.vector_store_id ||
                        combinedContextData?.vector_store_id ||
                        (isWorkspaceChat
                          ? currentWorkspace?.vector_store_id
                          : null)
                    ),
                    workspace_vector_store_id:
                      currentWorkspace?.vector_store_id,
                    workspace_id: workspaceIdToCheck,
                    is_workspace_chat:
                      combinedContextData?.is_workspace_chat || isWorkspaceChat,
                    from_workspace_chat:
                      combinedContextData?.from_workspace_chat ||
                      isWorkspaceChat,
                    workspace_initiated:
                      combinedContextData?.workspace_initiated ||
                      isWorkspaceChat,
                    // Canvas mode context - UPDATED: Force canvas tools when canvas mode is enabled
                    // CRITICAL FIX: Use effectiveCanvasModeEnabled (captured value) instead of reactive state
                    // This ensures canvas mode is used even if state was reset by new-chat-started event
                    isCanvasModeEnabled: effectiveCanvasModeEnabled,
                    canvasModePreference: effectiveCanvasModeEnabled,
                    forceCanvasCreate: effectiveCanvasModeEnabled, // Force canvas creation when canvas mode is active
                    // REMOVED: forceCanvasStream, useStructuredOutput - these were causing the feedback loop

                    // Tool selection control flags
                    hasExplicitToolSelection:
                      combinedContextData?.hasExplicitToolSelection ||
                      combinedContextData?.forceWebSearchTool === true ||
                      combinedContextData?.forceImageGenTool === true ||
                      combinedContextData?.forceCanvasCreate === true ||
                      combinedContextData?.forceImageEditTool === true ||
                      effectiveCanvasModeEnabled,

                    // File-related context
                    has_files: messageHasFiles,
                    file_count:
                      (storageFiles?.length || 0) +
                      (imageFiles?.length || 0) +
                      (pdfFiles?.length || 0),
                    vision_image_urls:
                      combinedContextData?.vision_image_urls &&
                      combinedContextData.vision_image_urls.length > 0
                        ? combinedContextData.vision_image_urls
                        : visionImageUrls,
                    has_images:
                      imageFiles.length > 0 ||
                      combinedContextData?.has_images === "true",
                    has_pdfs:
                      pdfFiles.length > 0 ||
                      combinedContextData?.has_pdfs === "true",
                    pdf_file_ids:
                      combinedContextData?.pdf_file_ids &&
                      combinedContextData.pdf_file_ids.length > 0
                        ? combinedContextData.pdf_file_ids
                        : pdfFileIds,
                    file_ids:
                      combinedContextData?.file_ids &&
                      combinedContextData.file_ids.length > 0
                        ? combinedContextData.file_ids
                        : filePathsWithOpenAIIds
                            .map((f) => f.file_id || f.openai_file_id)
                            .filter(Boolean),
                    storage_files: messageHasFiles
                      ? JSON.stringify(filePathsWithOpenAIIds)
                      : null,
                    // Pass through image edit data if present
                    ...(combinedContextData?.forceImageEditTool === true && {
                      forceImageEditTool: true,
                      imageEditData: combinedContextData.imageEditData,
                    }),
                    // Add image count for universal vision processing
                    image_count: imageFiles.length,
                  },
                  tool_choice: toolChoice,
                  messageId: assistantMessageId,
                  tokenCounter: tokenCounterRef.current,
                  navigate,
                  threadId: threadIdRef.current,
                  workspace_id: workspaceIdToCheck,
                  saveToDatabase: async (msgData) => {
                    try {
                      // Prevent duplicate saves
                      const savedMessageIds = new Set(
                        JSON.parse(
                          localStorage.getItem("savedMessageIds") || "[]"
                        )
                      );

                      if (savedMessageIds.has(msgData.id)) {
                        return { success: true, id: msgData.id, skipped: true };
                      }

                      if (
                        msgData.isStreaming === true ||
                        msgData.skipDatabaseSave === true
                      ) {
                        return { success: true, id: msgData.id };
                      }

                      // CRITICAL FIX: Validate role consistency to prevent user message corruption
                      if (msgData.role && msgData.content) {
                        // Check if this looks like user content but has assistant role
                        const isUserContent =
                          typeof msgData.content === "string" &&
                          !msgData.tool_calls &&
                          !msgData.metadata?.isFollowUpContent &&
                          !msgData.metadata?.isToolCallMessage;

                        if (
                          msgData.role === "assistant" &&
                          isUserContent &&
                          msgData.content.length < 200
                        ) {
                          // This might be a corrupted user message
                          console.warn(
                            "🚨 POTENTIAL ROLE CORRUPTION DETECTED:",
                            {
                              id: msgData.id,
                              role: msgData.role,
                              content: msgData.content.substring(0, 100),
                              hasToolCalls: !!msgData.tool_calls,
                              isFollowUp: !!msgData.metadata?.isFollowUpContent,
                              isToolCall: !!msgData.metadata?.isToolCallMessage,
                            }
                          );

                          // Check if there's already a user message with this content
                          const currentMessages =
                            useThreadStore.getState().messages || [];
                          const possibleUserMessage = currentMessages.find(
                            (m) =>
                              m.role === "user" &&
                              m.content === msgData.content &&
                              m.id !== msgData.id
                          );

                          if (possibleUserMessage) {
                            console.error(
                              "🚨 DUPLICATE USER CONTENT DETECTED - ABORTING SAVE:",
                              {
                                corruptedMessage: {
                                  id: msgData.id,
                                  role: msgData.role,
                                },
                                originalUserMessage: {
                                  id: possibleUserMessage.id,
                                  role: possibleUserMessage.role,
                                },
                              }
                            );
                            return {
                              success: false,
                              error: "Prevented duplicate user content save",
                            };
                          }
                        }
                      }

                      const isToolCallMessage =
                        (msgData.tool_calls &&
                          Array.isArray(msgData.tool_calls) &&
                          msgData.tool_calls.length > 0) ||
                        msgData.metadata?.isToolCallMessage === true;

                      const isFollowUpMessage =
                        msgData.metadata?.isFollowUpContent === true ||
                        msgData.metadata?.followUpCompleted === true;

                      if (isToolCallMessage || msgData._immediate === true) {
                        try {
                          const { supabase } = await import("@/lib/supabase");

                          const dbToolMsg = { ...msgData };
                          delete dbToolMsg.toolCalls; // Remove camelCase version that doesn't exist in schema
                          delete dbToolMsg.isStreaming;
                          delete dbToolMsg.isStreamComplete;
                          delete dbToolMsg.messageStatus;
                          delete dbToolMsg.skipDatabaseSave;
                          delete dbToolMsg._dependencies;
                          delete dbToolMsg._immediate;
                          delete dbToolMsg.threadId; // Use thread_id instead

                          dbToolMsg.message_id =
                            dbToolMsg.message_id || dbToolMsg.id;

                          dbToolMsg.metadata = {
                            ...(dbToolMsg.metadata || {}),
                            isToolCallMessage: true,
                            hasContentInToolCall: true,
                            save_sequence: 1,
                            save_priority: "critical",
                            timestamp: new Date().toISOString(),
                            toolSaveAttempt: "immediate",
                          };

                          if (
                            dbToolMsg.tool_calls &&
                            typeof dbToolMsg.tool_calls !== "string"
                          ) {
                            dbToolMsg.tool_calls = JSON.stringify(
                              dbToolMsg.tool_calls
                            );
                          }

                          const { error } = await supabase
                            .from("thread_messages")
                            .upsert([dbToolMsg], { onConflict: "id" });

                          if (error) {
                            console.error(
                              "Direct tool message save failed:",
                              error
                            );
                            await saveToolCallMessage({
                              ...msgData,
                              _immediate: true,
                              metadata: {
                                ...msgData.metadata,
                                fallbackSave: true,
                                isToolCallMessage: true,
                                hasContentInToolCall: true,
                              },
                            });
                          }

                          return {
                            success: true,
                            id: msgData.id,
                            immediate: true,
                          };
                        } catch (saveError) {
                          console.error(
                            "Critical tool message save failed:",
                            saveError
                          );

                          try {
                            const { supabase } = await import("@/lib/supabase");
                            const minimalToolMsg = {
                              id: msgData.id,
                              message_id: msgData.id,
                              thread_id: msgData.thread_id,
                              workspace_id:
                                msgData.workspace_id || workspaceIdToCheck,
                              role: "assistant",
                              content: msgData.content || " ",
                              created_at:
                                msgData.created_at || new Date().toISOString(),
                              updated_at: new Date().toISOString(),
                              metadata: {
                                isToolCallMessage: true,
                                hasContentInToolCall: true,
                                emergencySave: true,
                                timestamp: new Date().toISOString(),
                              },
                              tool_calls:
                                typeof msgData.tool_calls === "string"
                                  ? msgData.tool_calls
                                  : JSON.stringify(msgData.tool_calls || []),
                              tools_count:
                                msgData.tools_count ||
                                (Array.isArray(msgData.tool_calls)
                                  ? msgData.tool_calls.length
                                  : 0),
                            };

                            // Ensure no non-schema fields are included
                            delete minimalToolMsg.toolCalls;
                            delete minimalToolMsg.threadId;

                            const { error } = await supabase
                              .from("thread_messages")
                              .insert([minimalToolMsg]);

                            if (!error) {
                              return {
                                success: true,
                                id: msgData.id,
                                emergency: true,
                              };
                            }
                          } catch (emergencyError) {
                            console.error(
                              "All tool message save attempts failed:",
                              emergencyError
                            );
                          }
                        }
                      }

                      try {
                        // Clean message data before saving to remove non-schema fields
                        const cleanedMsgData = { ...msgData };
                        delete cleanedMsgData.toolCalls; // Remove camelCase version that doesn't exist in schema
                        delete cleanedMsgData.isStreaming;
                        delete cleanedMsgData.isStreamComplete;
                        delete cleanedMsgData.messageStatus;
                        delete cleanedMsgData.skipDatabaseSave;
                        delete cleanedMsgData._dependencies;
                        delete cleanedMsgData._immediate;
                        delete cleanedMsgData.threadId; // Use thread_id instead

                        // Ensure message_id is set
                        cleanedMsgData.message_id =
                          cleanedMsgData.message_id || cleanedMsgData.id;

                        // Ensure tool_calls is properly formatted as string if it exists
                        if (
                          cleanedMsgData.tool_calls &&
                          typeof cleanedMsgData.tool_calls !== "string"
                        ) {
                          cleanedMsgData.tool_calls = JSON.stringify(
                            cleanedMsgData.tool_calls
                          );
                        }

                        // Use the appropriate save function based on message type
                        if (cleanedMsgData.role === "user") {
                          await saveUserMessage(cleanedMsgData);
                        } else if (isToolCallMessage) {
                          await saveToolCallMessage(cleanedMsgData);
                        } else if (
                          isFollowUpMessage &&
                          cleanedMsgData.metadata?.toolCallMessageId
                        ) {
                          await saveFollowUpMessage(
                            cleanedMsgData,
                            cleanedMsgData.metadata.toolCallMessageId
                          );
                        } else {
                          // Default to assistant message for any other case
                          await saveAssistantMessage(cleanedMsgData);
                        }

                        // Mark message as saved to prevent duplicates
                        const savedMessageIds = new Set(
                          JSON.parse(
                            localStorage.getItem("savedMessageIds") || "[]"
                          )
                        );
                        savedMessageIds.add(msgData.id);
                        // Keep only last 100 message IDs to prevent localStorage bloat
                        const idsArray = Array.from(savedMessageIds);
                        if (idsArray.length > 100) {
                          idsArray.splice(0, idsArray.length - 100);
                        }
                        localStorage.setItem(
                          "savedMessageIds",
                          JSON.stringify(idsArray)
                        );

                        return { success: true, id: msgData.id };
                      } catch (saveError) {
                        console.error(
                          "Error saving message to database:",
                          saveError
                        );

                        try {
                          const { supabase } = await import("@/lib/supabase");

                          const dbSafeData = { ...msgData };
                          delete dbSafeData.toolCalls; // Remove camelCase version that doesn't exist in schema
                          delete dbSafeData.isStreaming;
                          delete dbSafeData.isStreamComplete;
                          delete dbSafeData.messageStatus;
                          delete dbSafeData.skipDatabaseSave;
                          delete dbSafeData._dependencies;
                          delete dbSafeData._immediate;
                          delete dbSafeData.threadId; // Use thread_id instead

                          dbSafeData.message_id =
                            dbSafeData.message_id || dbSafeData.id;

                          // Ensure tool_calls is properly formatted as string if it exists
                          if (
                            dbSafeData.tool_calls &&
                            typeof dbSafeData.tool_calls !== "string"
                          ) {
                            dbSafeData.tool_calls = JSON.stringify(
                              dbSafeData.tool_calls
                            );
                          }

                          const { error } = await supabase
                            .from("thread_messages")
                            .upsert([dbSafeData], {
                              onConflict: "id",
                              ignoreDuplicates: false,
                            });

                          if (error) {
                            console.error("Direct database save error:", error);
                            throw error;
                          }

                          return {
                            success: true,
                            id: msgData.id,
                            direct: true,
                          };
                        } catch (directError) {
                          console.error(
                            "All save methods failed:",
                            directError
                          );
                          throw directError;
                        }
                      }
                    } catch (error) {
                      console.error("Message database save error:", error);
                      return { success: false, error: error.message };
                    }
                  },
                });

                // Token recording is now handled in streamProviderChat for all providers
                // This ensures consistent token tracking across OpenAI, Anthropic, Google, etc.

                if (currentThread?.isNew) {
                  setCurrentThread({
                    ...currentThread,
                    isNew: false,
                  });
                }

                // Clear current files from file storage store after streaming completes
                try {
                  const { useFileStorageStore } = await import(
                    "@/store/fileStorageStore"
                  );
                  const { clearCurrentFiles } = useFileStorageStore.getState();
                  clearCurrentFiles();
                } catch (fileStorageError) {
                  console.warn(
                    "⚠️ Error clearing current files after streaming:",
                    fileStorageError
                  );
                }

                // Reset tool call state after message completion
                useThreadStore.setState({
                  currentToolCallName: null,
                  currentToolCallId: null,
                  currentToolCallOutput: null,
                });

                // ADDED: Immediate scroll to bottom when streaming completes
                if (scrollToBottom) {
                  setTimeout(() => {
                    scrollToBottom();
                  }, 100);
                }

                // CRITICAL: Recovery mechanism to ensure user messages are preserved after streaming
                setTimeout(() => {
                  const finalState = useThreadStore.getState();
                  const finalMessages = finalState.messages || [];
                  const currentStoreThreadId = finalState.currentThreadId;

                  // VALIDATION: Only recover message if we're still on the same thread
                  if (
                    currentStoreThreadId !== threadId &&
                    currentStoreThreadId !== null
                  ) {
                    console.log(
                      `[ChatContext] Skipping message recovery - thread changed from ${threadId} to ${currentStoreThreadId}`
                    );
                    // Clean up protection flag
                    localStorage.removeItem(`protectUserMessage_${userMsg.id}`);
                    return;
                  }

                  // Check if the user message still exists
                  const userMessageExists = finalMessages.some(
                    (msg) => msg.id === userMsg.id
                  );

                  if (!userMessageExists) {
                    console.error(
                      `🚨 User message ${userMsg.id} was lost during streaming! Recovering...`
                    );

                    // Try to recover from backup refs first
                    if (
                      backedUpMessagesRef.current?.some(
                        (msg) => msg.id === userMsg.id
                      )
                    ) {
                      const recoveredMessage = backedUpMessagesRef.current.find(
                        (msg) => msg.id === userMsg.id
                      );
                      useThreadStore.setState((state) => ({
                        ...state,
                        messages: [...(state.messages || []), recoveredMessage],
                        preventNewMessageClear: true,
                      }));
                    } else {
                      // Fallback: recreate the user message
                      useThreadStore.setState((state) => ({
                        ...state,
                        messages: [...(state.messages || []), userMsg],
                        preventNewMessageClear: true,
                      }));
                    }
                  }
                  // Clean up protection flag
                  localStorage.removeItem(`protectUserMessage_${userMsg.id}`);

                  // ADDED: Scroll to bottom after message recovery
                  if (scrollToBottom) {
                    scrollToBottom();
                  }
                }, 1000);

                // REMOVED: Automatic scrolling - let useChatScroll handle thread changes

                setTimeout(() => {
                  // REMOVED: Automatic scrolling

                  threadChangeRef.current = true;

                  try {
                    const currentThreadStore = useThreadStore.getState();
                    if (
                      (!currentThreadStore.messages ||
                        currentThreadStore.messages.length === 0) &&
                      messagesRef.current.length > 0
                    ) {
                      useThreadStore.setState({
                        messages: messagesRef.current,
                        preventNewMessageClear: true,
                      });
                    } else {
                      useThreadStore.setState({
                        preventNewMessageClear: true,
                      });
                    }
                  } catch (storeError) {
                    console.error(
                      "Error accessing thread store during navigation:",
                      storeError
                    );
                  }
                }, 150);

                streamSuccess = true;
              } catch (streamError) {
                console.error(
                  `Stream error (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`,
                  streamError
                );
                retryCount++;

                if (retryCount > MAX_RETRIES) {
                  console.error("All streaming attempts failed");

                  // Reset tool call state on error
                  useThreadStore.setState({
                    currentToolCallName: null,
                    currentToolCallId: null,
                    currentToolCallOutput: null,
                  });
                }
              }
            }
          } catch (error) {
            console.error("Send message error:", error);
            useThreadStore.setState({
              error: error.message,
              isLoading: false,
              isStreaming: false,
              streamStatus: "chat.failed",
              streamMessage: error.message,
              isStreamComplete: true,
            });
            throw error;
          }
        } catch (error) {
          console.error("Send message error:", error);
          useThreadStore.setState({
            error: error.message,
            isLoading: false,
            isStreaming: false,
            streamStatus: "chat.failed",
            streamMessage: error.message,
            isStreamComplete: true,
            // Reset tool call state on error
            currentToolCallName: null,
            currentToolCallId: null,
            currentToolCallOutput: null,
          });
          throw error;
        }
      } catch (error) {
        toast({
          title: "Failed to send message",
          description: error.message,
          variant: "destructive",
        });

        // Reset tool call state on error
        useThreadStore.setState({
          currentToolCallName: null,
          currentToolCallId: null,
          currentToolCallOutput: null,
        });
      }

      try {
        setTimeout(async () => {
          await ensureCriticalMessagesPreserved(
            currentThreadId,
            useThreadStore
          );
        }, 500);
      } catch (preserveError) {
        console.error("Error preserving critical messages:", preserveError);
      }

      try {
        // Use thread store for title generation instead of localStorage
        // CHANGED: Skip automatic title generation for deep research - it will be handled in Phase 2
        if (currentThreadId && content && !deepResearchEnabled) {
          const { enqueueTitleGeneration } = useThreadStore.getState();
          enqueueTitleGeneration(currentThreadId, content);
        }
      } catch (titleError) {
        console.error("Error enqueuing title generation:", titleError);
      }

      // Simplified thread vector store handling - no need for enable_retrieval flags
      try {
        const currentThreadStore = useThreadStore.getState();
        const currentThreadWithData =
          currentThreadStore.currentThread ||
          (await currentThreadStore.fetchThread(threadId));

        if (currentThreadWithData && currentThreadWithData.vector_store_id) {
          if (!contextData.vector_store_id) {
            contextData.vector_store_id = currentThreadWithData.vector_store_id;
          }
          if (!contextData.thread_vector_store_id) {
            contextData.thread_vector_store_id =
              currentThreadWithData.vector_store_id;
          }
          if (!contextData.message_content && content) {
            contextData.message_content = content;
          }
        }
      } catch (error) {
        console.error("Error checking thread for vector_store_id:", error);
      }
    },
    [
      currentAssistant,
      userId,
      currentWorkspaceId,
      preventAutomaticThread,
      enhancedUpdateMessageState,
      currentThread,
      setCurrentThread,
      navigate,
      location.pathname,

      scrollToBottom,
      toast,

      preserveMessagesBeforeSend,
    ]
  );

  saveEditedMessage = useCallback(
    async (newContent) => {
      if (!isEditingMessage || !editingMessageId) {
        console.warn("Cannot save edit - no active editing session");
        return false;
      }

      try {
        const messageToEdit = messages.find(
          (msg) => msg.id === editingMessageId
        );

        if (!messageToEdit) {
          throw new Error(`Message with ID ${editingMessageId} not found`);
        }

        // Safety check: Only allow editing user messages
        if (messageToEdit.role !== "user") {
          console.error("Cannot edit non-user message:", {
            messageId: messageToEdit.id,
            role: messageToEdit.role,
          });
          return false;
        }

        const allMessages = useThreadStore.getState().messages || [];

        const editedMessageIndex = allMessages.findIndex(
          (msg) => msg.id === editingMessageId
        );

        if (editedMessageIndex === -1) {
          throw new Error(
            `Message with ID ${editingMessageId} not found in thread store`
          );
        }

        const subsequentMessages = allMessages.slice(editedMessageIndex + 1);

        const subsequentMessageIds = subsequentMessages.map((msg) => msg.id);

        if (subsequentMessageIds.length > 0) {
          try {
            const { supabase } = await import("@/lib/supabase");

            for (const msgId of subsequentMessageIds) {
              const { error } = await supabase
                .from("thread_messages")
                .delete()
                .eq("id", msgId)
                .eq("thread_id", currentThreadId);

              if (error) {
                console.error(`Error deleting message ${msgId}:`, error);
              }
            }
          } catch (deleteError) {
            console.error(
              "Error deleting subsequent messages from database:",
              deleteError
            );
          }
        }

        try {
          const { supabase } = await import("@/lib/supabase");
          const { error: updateError } = await supabase
            .from("thread_messages")
            .update({
              content: newContent,
              updated_at: new Date().toISOString(),
              metadata: {
                ...(messageToEdit.metadata || {}),
                edited: true,
                edited_at: new Date().toISOString(),
                original_content: messageToEdit.content,
              },
            })
            .eq("id", editingMessageId)
            .eq("thread_id", currentThreadId);

          if (updateError) {
            console.error("Error updating edited message:", updateError);
          }
        } catch (updateError) {
          console.error("Error updating message in database:", updateError);
        }

        // Update the edited message in local state
        const updatedMessages = allMessages.slice(0, editedMessageIndex + 1);
        updatedMessages[editedMessageIndex] = {
          ...messageToEdit,
          content: newContent,
          metadata: {
            ...(messageToEdit.metadata || {}),
            edited: true,
            edited_at: new Date().toISOString(),
          },
        };

        useThreadStore.setState({
          messages: updatedMessages,
          preventNewMessageClear: true,
        });

        setIsEditingMessage(false);
        setEditingMessageId(null);
        setEditingMessageContent("");

        if (scrollContainerRef.current) {
          scrollContainerRef.current.dataset.editingMessageId = "";
          scrollContainerRef.current.dataset.editingActive = "false";
        }

        // FIXED: Instead of calling handleSendMessage (which creates a new user message),
        // directly trigger AI response with the updated message history
        try {
          const modelId = currentAssistant?.model || "gpt-4o-mini";
          const modelStore = useModelStore.getState();
          let providerName = modelStore.getEffectiveProvider();

          const assistantMessageId = fileUtils.generateUUID();

          // Initialize streaming state
          const streamState = initializeStreamState();
          useThreadStore.setState(streamState);

          enhancedUpdateMessageState({
            id: assistantMessageId,
            role: "assistant",
            content: "",
            created_at: new Date().toISOString(),
            isStreaming: true,
            messageStatus: "created",
            skipDatabaseSave: true,
            metadata: {
              provider: providerName,
              model: modelId,
              isResponseToEdit: true,
              editedMessageId: editingMessageId,
            },
          });

          // Get updated message history for AI response
          let messageHistory = updatedMessages;

          // Prepare assistant tools
          let assistantTools = [];
          if (currentAssistant?.tools && currentAssistant.tools.length > 0) {
            try {
              assistantTools = convertAssistantTools(
                currentAssistant.tools.filter(Boolean)
              );
            } catch (error) {
              console.error("Error converting assistant tools:", error);
            }
          }

          // Get workspace context
          const workspaceIdToCheck = currentWorkspaceId;
          const instructions =
            currentAssistant?.instructions || "You are a helpful assistant.";

          // Stream AI response to the edited message
          await streamProviderChat({
            providerName: providerName,
            modelName: modelId,
            messages: messageHistory,
            instructions: instructions,
            tools: assistantTools,
            threadStore: {
              getState: useThreadStore.getState,
              setState: useThreadStore.setState,
            }, // Pass store methods that work outside React components
            updateMessageState: enhancedUpdateMessageState,
            userId: userId,
            contextData: {
              isResponseToEdit: true,
              editedMessageId: editingMessageId,
              editTimestamp: new Date().toISOString(),
              workspace_id: workspaceIdToCheck,
            },
            messageId: assistantMessageId,
            threadId: currentThreadId,
            workspace_id: workspaceIdToCheck,
            saveToDatabase: async (msgData) => {
              try {
                if (
                  msgData.isStreaming === true ||
                  msgData.skipDatabaseSave === true
                ) {
                  return { success: true, id: msgData.id };
                }

                // Use appropriate save function based on message role
                if (msgData.role === "user") {
                  await saveUserMessage(msgData);
                } else if (msgData.role === "assistant") {
                  await saveAssistantMessage(msgData);
                } else {
                  console.warn(
                    "Unknown message role in saveToDatabase:",
                    msgData.role
                  );
                }
                return { success: true, id: msgData.id };
              } catch (error) {
                console.error("Message database save error:", error);
                return { success: false, error: error.message };
              }
            },
          });
        } catch (streamError) {
          console.error(
            "Error streaming response to edited message:",
            streamError
          );
          toast({
            title: "Failed to get AI response",
            description:
              "The message was edited successfully, but getting a new AI response failed.",
            variant: "destructive",
          });
        }

        // ADDED: Scroll to bottom after edit response completes
        setTimeout(() => {
          if (scrollToBottom) {
            scrollToBottom();
          }
        }, 500);

        return true;
      } catch (error) {
        console.error("Error saving edited message:", error);
        toast({
          title: "Failed to save edit",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
    },
    [
      isEditingMessage,
      editingMessageId,
      messages,
      currentThreadId,
      currentAssistant,
      currentWorkspaceId,
      userId,
      enhancedUpdateMessageState,
      toast,
    ]
  );

  /**
   * Handle upgrading to premium
   */
  const handleUpgrade = useCallback(() => {
    sessionStorage.setItem("return_url", window.location.pathname);

    setIsPremiumDialog(false);

    navigate("/pricing");
  }, [navigate]);

  // Track which threads have been processed to prevent duplicate processing
  const processedThreadsRef = useRef(new Set());

  useEffect(() => {
    if (messages && Array.isArray(messages)) {
      const messageCount = messages.length;
      const hasUserMessages = messages.some((m) => m.role === "user");
      const hasAssistantMessages = messages.some((m) => m.role === "assistant");

      if (
        messageCount > 0 &&
        (!messagesRef.current || messagesRef.current.length < messageCount)
      ) {
        messagesRef.current = [...messages];
      }
    }
  }, [messages]);

  /**
   * Clear thread lock references
   * Called when navigating away from a thread to prevent stale references
   */
  const clearThreadLock = useCallback(() => {
    threadIdRef.current = null;
    deepResearchThreadIdRef.current = null;
    if (import.meta.env.DEV) {
      console.log("[ChatContext] Thread lock cleared");
    }
  }, []);

  /**
   * Handle aborting the current stream
   */
  const handleAbortStream = useCallback(() => {
    console.log(
      "[ChatContext] handleAbortStream called - aborting running query"
    );
    const threadState = useThreadStore.getState();

    // Case 1: Stream is active - abort the abortController
    if (threadState.abortController) {
      console.log("[ChatContext] AbortController found, aborting stream...");
      threadState.abortController.abort("User requested abort");

      // Update thread state to reflect abort
      useThreadStore.setState((state) => ({
        ...state,
        isStreaming: false,
        streamStatus: "aborted",
        messageStatus: "aborted",
        isStreamComplete: true,
        abortController: null,
        // Reset tool call state on abort
        currentToolCallName: null,
        currentToolCallId: null,
        currentToolCallOutput: null,
      }));

      // Clear the local abort controller state
      setCurrentAbortController(null);
      console.log("[ChatContext] Stream aborted successfully");
      return;
    }

    // Case 2: No abortController yet - clear optimistic messages (pre-stream cancellation)
    const currentThreadId = threadState.currentThreadId;
    if (currentThreadId) {
      const optimisticMessages =
        threadState.getOptimisticMessages?.(currentThreadId) || [];
      const hasOptimisticUserMessage = optimisticMessages.some(
        (msg) => msg.isOptimistic && msg.role === "user"
      );

      if (hasOptimisticUserMessage) {
        // Clear optimistic user messages for this thread
        const updatedOptimistic = new Map(
          threadState.optimisticMessages || new Map()
        );
        const threadMessages = updatedOptimistic.get(currentThreadId) || [];
        const filtered = threadMessages.filter(
          (msg) => !(msg.isOptimistic && msg.role === "user")
        );
        updatedOptimistic.set(currentThreadId, filtered);

        useThreadStore.setState({
          optimisticMessages: updatedOptimistic,
          isStreaming: false,
          streamStatus: "cancelled",
          messageStatus: "cancelled",
        });

        console.log(
          "[ChatContext] Cleared optimistic user message - request cancelled before streaming started"
        );
        return;
      }
    }

    // Case 3: Check for stale streaming state (after page reload)
    // This handles the case where isStreaming is true but abortController was lost
    if (
      threadState.hasStaleStreamingState &&
      threadState.hasStaleStreamingState()
    ) {
      console.log("[ChatContext] Detected stale streaming state, clearing...");
      threadState.clearStaleStreamingState();
      console.log("[ChatContext] Stale streaming state cleared successfully");
      return;
    }

    console.warn("[ChatContext] handleAbortStream called but nothing to abort");
  }, []);

  // CRITICAL FIX: Auto-submit pending workspace messages when thread loads
  // This is more reliable than event-based approach since it checks directly in ChatContext
  // Placed after handleSendMessage is defined to avoid initialization errors
  useEffect(() => {
    if (!currentThreadId || location.pathname === "/chat/new") {
      return;
    }

    const pendingWorkspaceMessage = localStorage.getItem(
      "pendingWorkspaceMessage"
    );
    const hasNoMessages = !messages || messages.length === 0;

    if (pendingWorkspaceMessage && hasNoMessages) {
      // Small delay to ensure thread is fully loaded and ChatContext is ready
      const timeoutId = setTimeout(() => {
        // Double-check conditions before sending
        const stillPending = localStorage.getItem("pendingWorkspaceMessage");
        const currentState = useThreadStore.getState();
        const stillNoMessages =
          !currentState.messages || currentState.messages.length === 0;
        const stillOnSameThread =
          currentState.currentThreadId === currentThreadId;

        if (stillPending && stillNoMessages && stillOnSameThread) {
          // Clear localStorage before sending to prevent duplicate sends
          localStorage.removeItem("pendingWorkspaceMessage");
          localStorage.removeItem("pending_thread_title");

          // Send the message using handleSendMessage
          handleSendMessage({
            content: stillPending,
            files: [],
            storageFiles: [],
          });
        }
      }, 200); // Slightly longer delay to ensure everything is ready

      return () => clearTimeout(timeoutId);
    }
  }, [currentThreadId, messages?.length, location.pathname, handleSendMessage]);

  /**
   * Handle model rerun without creating duplicate user messages
   * Similar to saveEditedMessage but for model switching scenarios
   */
  const handleModelRerun = useCallback(
    async (messageId, _messageContent) => {
      try {
        const messageToRerun = messages.find((msg) => msg.id === messageId);

        if (!messageToRerun) {
          throw new Error(`Message with ID ${messageId} not found`);
        }

        // Safety check: Only allow rerunning user messages
        if (messageToRerun.role !== "user") {
          console.error("Cannot rerun non-user message:", {
            messageId: messageToRerun.id,
            role: messageToRerun.role,
          });
          return false;
        }

        const allMessages = useThreadStore.getState().messages || [];

        const rerunMessageIndex = allMessages.findIndex(
          (msg) => msg.id === messageId
        );

        if (rerunMessageIndex === -1) {
          throw new Error(
            `Message with ID ${messageId} not found in thread store`
          );
        }

        const subsequentMessages = allMessages.slice(rerunMessageIndex + 1);
        const subsequentMessageIds = subsequentMessages.map((msg) => msg.id);

        // Delete subsequent messages from database
        if (subsequentMessageIds.length > 0) {
          try {
            const { supabase } = await import("@/lib/supabase");

            for (const msgId of subsequentMessageIds) {
              const { error } = await supabase
                .from("thread_messages")
                .delete()
                .eq("id", msgId)
                .eq("thread_id", currentThreadId);

              if (error) {
                console.error(`Error deleting message ${msgId}:`, error);
              }
            }
          } catch (deleteError) {
            console.error(
              "Error deleting subsequent messages from database:",
              deleteError
            );
          }
        }

        // Update the message metadata to indicate it was rerun
        try {
          const { supabase } = await import("@/lib/supabase");
          const { error: updateError } = await supabase
            .from("thread_messages")
            .update({
              updated_at: new Date().toISOString(),
              metadata: {
                ...(messageToRerun.metadata || {}),
                rerun: true,
                rerun_at: new Date().toISOString(),
                rerun_with_model: currentAssistant?.model,
              },
            })
            .eq("id", messageId)
            .eq("thread_id", currentThreadId);

          if (updateError) {
            console.error("Error updating rerun message:", updateError);
          }
        } catch (updateError) {
          console.error("Error updating message in database:", updateError);
        }

        // Update local state - keep only messages up to and including the rerun message
        const updatedMessages = allMessages.slice(0, rerunMessageIndex + 1);
        updatedMessages[rerunMessageIndex] = {
          ...messageToRerun,
          metadata: {
            ...(messageToRerun.metadata || {}),
            rerun: true,
            rerun_at: new Date().toISOString(),
            rerun_with_model: currentAssistant?.model,
          },
        };

        useThreadStore.setState({
          messages: updatedMessages,
          preventNewMessageClear: true,
        });

        // Now trigger AI response with the updated assistant model (which should already be set by ModelSwitchAndRerun)
        try {
          const modelId = currentAssistant?.model || "gpt-4o-mini";
          const modelStore = useModelStore.getState();
          let providerName = modelStore.getEffectiveProvider();

          const assistantMessageId = fileUtils.generateUUID();

          // Initialize streaming state
          const streamState = initializeStreamState();
          useThreadStore.setState(streamState);

          enhancedUpdateMessageState({
            id: assistantMessageId,
            role: "assistant",
            content: "",
            created_at: new Date().toISOString(),
            isStreaming: true,
            messageStatus: "created",
            skipDatabaseSave: true,
            metadata: {
              provider: providerName,
              model: modelId,
              isResponseToRerun: true,
              rerunMessageId: messageId,
            },
          });

          // Get updated message history for AI response
          let messageHistory = updatedMessages;

          // Prepare assistant tools
          let assistantTools = [];
          if (currentAssistant?.tools && currentAssistant.tools.length > 0) {
            try {
              assistantTools = convertAssistantTools(
                currentAssistant.tools.filter(Boolean)
              );
            } catch (error) {
              console.error("Error converting assistant tools:", error);
            }
          }

          // Get workspace context
          const workspaceIdToCheck = currentWorkspaceId;
          const instructions =
            currentAssistant?.instructions || "You are a helpful assistant.";

          // Stream AI response with the new model
          await streamProviderChat({
            providerName: providerName,
            modelName: modelId,
            messages: messageHistory,
            instructions: instructions,
            tools: assistantTools,
            threadStore: useThreadStore.setState,
            updateMessageState: enhancedUpdateMessageState,
            userId: userId,
            contextData: {
              isResponseToRerun: true,
              rerunMessageId: messageId,
              rerunTimestamp: new Date().toISOString(),
              workspace_id: workspaceIdToCheck,
            },
            messageId: assistantMessageId,
            threadId: currentThreadId,
            workspace_id: workspaceIdToCheck,
            saveToDatabase: async (msgData) => {
              try {
                if (
                  msgData.isStreaming === true ||
                  msgData.skipDatabaseSave === true
                ) {
                  return { success: true, id: msgData.id };
                }

                // Use appropriate save function based on message role
                if (msgData.role === "user") {
                  await saveUserMessage(msgData);
                } else if (msgData.role === "assistant") {
                  await saveAssistantMessage(msgData);
                } else {
                  console.warn(
                    "Unknown message role in saveToDatabase:",
                    msgData.role
                  );
                }
                return { success: true, id: msgData.id };
              } catch (error) {
                console.error("Message database save error:", error);
                return { success: false, error: error.message };
              }
            },
          });
        } catch (streamError) {
          console.error(
            "Error streaming response to rerun message:",
            streamError
          );
          toast({
            title: "Failed to get AI response",
            description:
              "The model was switched successfully, but getting a new AI response failed.",
            variant: "destructive",
          });
        }

        // ADDED: Scroll to bottom after rerun response completes
        setTimeout(() => {
          if (scrollToBottom) {
            scrollToBottom();
          }
        }, 500);

        return true;
      } catch (error) {
        console.error("Error handling model rerun:", error);
        toast({
          title: "Failed to rerun with new model",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
    },
    [
      messages,
      currentThreadId,
      currentAssistant,
      currentWorkspaceId,
      userId,
      enhancedUpdateMessageState,
      toast,
    ]
  );

  // Add effect to clear thread refs when thread changes
  useEffect(() => {
    // Clear thread refs when currentThreadId changes to prevent stale references
    if (currentThreadId !== threadIdRef.current) {
      threadIdRef.current = null;
      deepResearchThreadIdRef.current = null;
    }
  }, [currentThreadId]);

  // Add effect to clear thread refs and deep research state when thread changes
  useEffect(() => {
    // Clear thread refs when currentThreadId changes to prevent stale references
    if (
      currentThreadId !== threadIdRef.current &&
      threadIdRef.current !== null
    ) {
      threadIdRef.current = null;
      deepResearchThreadIdRef.current = null;

      // Also disable deep research when switching threads to prevent confusion
      if (isDeepResearchEnabled) {
        forceDisableDeepResearch();
      }
    }
  }, [currentThreadId, isDeepResearchEnabled, forceDisableDeepResearch]);

  // Check if there's an active deep research in progress
  const isDeepResearchInProgress = useMemo(() => {
    return messagesWithOptimistic?.some(
      (msg) =>
        msg?.metadata?.isDeepResearch === true &&
        msg?.metadata?.isDeepResearchInProgress === true &&
        msg?.isStreaming === true
    );
  }, [messagesWithOptimistic]);

  /**
   * Retry the last message after an error (Claude pattern)
   * Removes the error assistant message and resends the last user message
   */
  const retryLastMessage = useCallback(async () => {
    const messages = useThreadStore.getState().messages || [];
    const lastUserMessage = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUserMessage) return;

    // Remove error assistant messages
    useThreadStore.setState((state) => ({
      messages: state.messages.filter((m) => !(m.role === "assistant" && m.isError)),
    }));

    // Resend the last user message
    await handleSendMessage({ content: lastUserMessage.content });
  }, [handleSendMessage]);

  const contextValue = useMemo(
    () => ({
      currentThreadId,
      currentThread,
      isStreaming,
      messages: messagesWithOptimistic, // Use merged messages with optimistic updates
      messageStatus,
      streamStatus,
      currentMessageId,
      isEditingMessage,
      editingMessageId,
      editingMessageContent,
      isRenderingCodeBlocks,
      isHandlingMessage: isStreaming || isEditedMessageInProgress, // Calculate isHandlingMessage inline
      isPremiumDialog,

      isDeepResearchEnabled,
      isDeepResearchInProgress, // Add flag to indicate active deep research
      deepResearchPhase,
      originalDeepResearchQuery,
      clarifyingQuestions,
      userClarifications,
      activeDeepResearch,
      deepResearchSidebarOpen,
      sourcesSidebarOpen,
      activeSources,

      imageArtifactSidebarOpen,
      activeImageArtifact,

      // Canvas mode state and functions
      isCanvasModeEnabled,
      pendingCanvasContent,
      toggleCanvasMode,
      enableCanvasMode,
      disableCanvasMode,

      scrollContainerRef,
      bottomRef,

      startEditingMessage,
      cancelEditingMessage,
      saveEditedMessage,
      handleModelRerun,

      // Reply to message state and functions
      replyToMessage,
      startReplyToMessage,
      cancelReplyToMessage,

      scrollToBottom,
      handleSendMessage,
      handleAbortStream,
      clearThreadLock,
      setIsRenderingCodeBlocks,
      setIsPremiumDialog,
      handleUpgrade,
      retryLastMessage,

      toggleDeepResearch,
      forceDisableDeepResearch,
      optOutOfDeepResearch,
      generateClarifyingQuestions,
      rewriteQueryWithClarifications,
      setActiveDeepResearch,
      setDeepResearchSidebarOpen,

      setSourcesSidebarOpen,
      setActiveSources,

      setImageArtifactSidebarOpen,
      setActiveImageArtifact,

      // Removed optimized functions that were deleted
    }),
    [
      currentThreadId,
      currentThread,
      isStreaming,
      messagesWithOptimistic, // Use merged messages with optimistic updates
      messageStatus,
      streamStatus,
      currentMessageId,
      isEditingMessage,
      editingMessageId,
      editingMessageContent,
      isRenderingCodeBlocks,
      isEditedMessageInProgress, // Use isEditedMessageInProgress directly
      isPremiumDialog,

      isDeepResearchEnabled,
      isDeepResearchInProgress, // Add dependency
      deepResearchPhase,
      originalDeepResearchQuery,
      clarifyingQuestions,
      userClarifications,
      activeDeepResearch,
      deepResearchSidebarOpen,

      sourcesSidebarOpen,
      activeSources,

      imageArtifactSidebarOpen,
      activeImageArtifact,

      // Canvas mode dependencies
      isCanvasModeEnabled,
      pendingCanvasContent,
      toggleCanvasMode,
      enableCanvasMode,
      disableCanvasMode,

      scrollToBottom,
      handleSendMessage,
      handleAbortStream,
      handleUpgrade,
      retryLastMessage,
      toggleDeepResearch,
      forceDisableDeepResearch,
      optOutOfDeepResearch,
      generateClarifyingQuestions,
      rewriteQueryWithClarifications,
      setActiveDeepResearch,
      setDeepResearchSidebarOpen,
      startEditingMessage,
      cancelEditingMessage,
      saveEditedMessage,
      handleModelRerun,
      replyToMessage,
      startReplyToMessage,
      cancelReplyToMessage,
      // Removed deleted function dependencies
    ]
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use chat context
 *
 * @returns {Object} Chat context value
 */
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    // Return null when not in a ChatProvider (safe fallback)
    return null;
  }
  return context;
}

export default ChatContext;
